import { ORG_COST_OPTIMIZATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF } from './approved-source-systems';
import type {
  ApprovedFormulaDefinition,
  ApprovedFormulaEvidenceArtifact,
  ApprovedFormulaEvidenceValidationBlock,
  ApprovedFormulaGoldenTrace,
  ApprovedFormulaImplementationDigestInput,
  ApprovedFormulaReconciliationRule,
  ApprovedFormulaResult,
} from './approved-formulas';
import { buildOrgTimeToRealizeSchedule } from './org-time-to-realize-schedule';

const ORG_ANALYSIS_CONTEXT_KIND = 'decision_agent:org_analysis_optimization';
const ORG_ANALYSIS_FORMULA_VERSION = '1.0.0';
const FORMULA_RUNTIME_VERSION = 'approved_formula_runtime_v1';
const CANONICALIZER_VERSION = 'approved_formula_canonicalizer_v1';
const EPSILON = 0.000001;

export const ORG_ANALYSIS_DISTINCT_SCENARIO_FORMULA_IDS = Object.freeze([
  'org_time_to_realize_v1',
  'org_delayering_net_ebitda_scenario_v1',
  'org_compare_cost_structure_options_v1',
  'org_savings_work_redesign_gate_v1',
  'org_people_risk_adjusted_net_ebitda_v1',
  'org_options_decision_dashboard_v1',
  'org_recommended_option_rationale_v1',
  'org_execution_risk_time_to_realize_v1',
  'org_realization_confidence_band_v1',
] as const);

export type OrgAnalysisDistinctScenarioFormulaId =
  (typeof ORG_ANALYSIS_DISTINCT_SCENARIO_FORMULA_IDS)[number];

export type OrgAnalysisSubstrateAnchor = Readonly<{
  tenantId: string;
  orgStateId: string;
  reportingCutAsOf: string;
  positionCount: number;
  fullyLoadedAnnualCostUsd: number;
  evidenceRefId: string;
  formulaRef: 'org_position_cost_rollup_v1@1.0.0';
  oracleReceiptId: string;
  oracleRung: 'R2' | 'R3';
}>;

export const ORG_ANALYSIS_FIXTURE_SUBSTRATE_ANCHOR: OrgAnalysisSubstrateAnchor = Object.freeze({
  tenantId: 'helix_retail',
  orgStateId: 'cmq9immcw00033q6vani2p5dz',
  reportingCutAsOf: '2026-07-10',
  positionCount: 11_855,
  fullyLoadedAnnualCostUsd: 941_920_000,
  evidenceRefId: 'docs/capabilities/audit-20260710/trust-scorecard.json',
  formulaRef: 'org_position_cost_rollup_v1@1.0.0',
  oracleReceiptId: 'trust-scorecard:org-analysis-optimization:R3',
  oracleRung: 'R3',
});

export type OrgPositionCostRollupInput = Readonly<{
  positionIds: readonly string[];
  costRows: readonly Readonly<{
    positionId: string;
    fullyLoadedAnnualCostUsd: number;
    currency: 'USD';
  }>[];
  reportingCutAsOf: string;
}>;

export type OrgPositionCostRollupOutput = Readonly<{
  primaryMetricKey: 'fully_loaded_org_cost_usd';
  primaryMetricValue: number;
  positionCount: number;
  fullyLoadedAnnualCostUsd: number;
  missingCostPositionIds: readonly string[];
}>;

/**
 * One governed org-cost sub-lever row, exactly as the `org_cost_sub_lever_buildup`
 * evidence artifact carries it.
 *
 * SIGNS. `grossRunRateCostReductionUsd` is annual run-rate cost REMOVED, so a
 * positive value improves operating income. `oneTimeCostUsd` is cost INCURRED to
 * remove it, so a positive value reduces the net. Both are non-negative by
 * contract; a lever that adds run-rate cost is not a cost sub-lever and is
 * refused rather than netted.
 *
 * `deltaHeadcount` MUST be 0. This claim is the non-workforce org-cost lever set;
 * anything that moves headcount belongs to the workforce-reduction module and is
 * a separate claim that must never be summed with this one.
 */
export type OrgCostSubLeverContributionRow = Readonly<{
  subLeverId: string;
  deltaHeadcount: number;
  grossRunRateCostReductionUsd: number;
  oneTimeCostUsd: number;
  currency: 'USD';
}>;

export type OrgCostSubLeverNetRunRateContributionInput = Readonly<{
  orgStateId: string;
  reportingCutAsOf: string;
  contributions: readonly OrgCostSubLeverContributionRow[];
}>;

export type OrgCostSubLeverNetRunRateContributionOutput = Readonly<{
  primaryMetricKey: 'org_cost_sub_lever_net_run_rate_contribution_usd';
  primaryMetricValue: number;
  subLeverCount: number;
  grossRunRateCostReductionUsd: number;
  oneTimeCostUsd: number;
  orgCostSubLeverNetRunRateContributionUsd: number;
  subLeverIds: readonly string[];
}>;

export type OrgTimeToRealizeInput = Readonly<{
  substrateAnchor: OrgAnalysisSubstrateAnchor;
  evidenceRefIds: readonly string[];
  optionId: string;
  optionVersion: number;
  workPackages: readonly Readonly<{
    workPackageId: string;
    predecessorIds: readonly string[];
    durationDays: number;
    ownerRoleId: string;
    startsBenefit: boolean;
    requiredForFullRunRate: boolean;
    cumulativeRunRateFraction: number | null;
  }>[];
  reportingCutAsOf: string;
}>;

export type OrgTimeToRealizeOutput = Readonly<{
  primaryMetricKey: 'full_run_rate_day';
  primaryMetricValue: number;
  firstBenefitDay: number;
  fullRunRateDay: number;
  criticalPathWorkPackageIds: readonly string[];
  earliestFinishDayByWorkPackageId: Readonly<Record<string, number>>;
  realizationCurve: readonly Readonly<{
    workPackageId: string;
    completionDay: number;
    cumulativeRunRateFraction: number;
  }>[];
  scheduleGapCodes: readonly string[];
}>;

export type OrgDelayeringPosition = Readonly<{
  positionId: string;
  managerPositionId: string | null;
  fullyLoadedAnnualCostUsd: number;
  assignedWorkPackageIds: readonly string[];
}>;

export type OrgDelayeringGraphAction = Readonly<{
  actionId: string;
  actionType: 'remove_position' | 'add_position' | 'change_manager';
  positionId: string;
  targetManagerPositionId: string | null;
  fullyLoadedAnnualCostUsd: number | null;
  assignedWorkPackageIds: readonly string[];
  reassignWorkToPositionId: string | null;
}>;

export type OrgDelayeringNetEbitdaScenarioInput = Readonly<{
  substrateAnchor: OrgAnalysisSubstrateAnchor;
  evidenceRefIds: readonly string[];
  optionId: string;
  optionVersion: number;
  optionType: 'delayering';
  valueDomain: 'org_cost';
  baselinePositions: readonly OrgDelayeringPosition[];
  actions: readonly OrgDelayeringGraphAction[];
  recurringLeakageUsd: number;
  oneTimeCashUsdByMonth: readonly number[];
  monthlyNetCashUsd: readonly number[];
  discountRateAnnual: number;
  capacityRows: readonly Readonly<{
    workPackageId: string;
    requiredFte: number;
    assignedFte: number;
  }>[];
  serviceOutcomeRows: readonly Readonly<{
    metricId: string;
    baselineValue: number;
    scenarioValue: number;
    direction: 'higher_is_better' | 'lower_is_better';
  }>[];
  financeDefinitionRef: string;
  mappedGlAccountIds: readonly string[];
  financeReconciled: boolean;
  reportingCutAsOf: string;
}>;

export type OrgDelayeringNetEbitdaScenarioOutput = Readonly<{
  primaryMetricKey: 'org_delayering_net_run_rate_usd';
  primaryMetricValue: number;
  baselinePositionCount: number;
  baselineFullyLoadedOrgCostUsd: number;
  substratePositionCount: number;
  substrateFullyLoadedOrgCostUsd: number;
  afterPositionCount: number;
  afterFullyLoadedOrgCostUsd: number;
  netRunRateUsd: number;
  oneTimeCashUsd: number;
  inPeriodNetCashUsd: number;
  npvUsd: number;
  capacityGapFte: number;
  serviceDeltaPct: number;
  ebitdaLabel: 'finance_reconciled_net_ebitda';
  financeDefinitionRef: string;
  mappedGlAccountIds: readonly string[];
  graphDelta: Readonly<{
    beforePositionCount: number;
    afterPositionCount: number;
    beforeManagerCount: number;
    afterManagerCount: number;
    beforeMaxDepth: number;
    afterMaxDepth: number;
    removedPositionIds: readonly string[];
    addedPositionIds: readonly string[];
    reparentedPositionIds: readonly string[];
  }>;
}>;

export type OrgCostStructureOptionVector = Readonly<{
  optionId: string;
  optionType: 'do_nothing' | 'delayering' | 'shared_services' | 'workforce_reduction';
  valueDomain: 'org_cost' | 'workforce';
  netRunRateUsd: number;
  oneTimeCashUsd: number;
  npvUsd: number;
  paybackMonth: number;
  firstBenefitMonth: number;
  fullRunRateMonth: number;
  scheduleP50Month: number;
  scheduleP80Month: number;
  capacityGapFte: number;
  serviceDeltaPct: number;
  legalGate: 'pass' | 'block';
  confidenceRung: 'R1' | 'R2' | 'R3' | 'R4' | 'R5';
}>;

export type OrgCompareCostStructureOptionsInput = Readonly<{
  substrateAnchor: OrgAnalysisSubstrateAnchor;
  evidenceRefIds: readonly string[];
  options: readonly OrgCostStructureOptionVector[];
  reportingCutAsOf: string;
}>;

export type OrgCompareCostStructureOptionsOutput = Readonly<{
  primaryMetricKey: 'pareto_option_count';
  primaryMetricValue: number;
  optionCount: number;
  paretoOptionIds: readonly string[];
  blockedOptionIds: readonly string[];
  options: readonly OrgCostStructureOptionVector[];
  additiveValueUsd: null;
}>;

export type OrgSavingsWorkRedesignGateInput = Readonly<{
  substrateAnchor: OrgAnalysisSubstrateAnchor;
  evidenceRefIds: readonly string[];
  contributions: readonly Readonly<{
    contributionId: string;
    valueDomain: 'org_cost' | 'workforce';
    netRunRateUsd: number;
    workBound: boolean;
    resourceOrVendorActionBound: boolean;
    financeAccountBound: boolean;
    ownerBound: boolean;
    milestoneBound: boolean;
    overlapKeysBound: boolean;
  }>[];
  reportingCutAsOf: string;
}>;

export type OrgSavingsWorkRedesignGateOutput = Readonly<{
  primaryMetricKey: 'bankable_org_net_run_rate_usd';
  primaryMetricValue: number;
  bankableNetRunRateUsd: number;
  withheldNetRunRateUsd: number;
  eligibleContributionIds: readonly string[];
  withheldPredicatesByContributionId: Readonly<Record<string, readonly string[]>>;
}>;

export type OrgPeopleRiskAdjustedNetEbitdaInput = Readonly<{
  substrateAnchor: OrgAnalysisSubstrateAnchor;
  evidenceRefIds: readonly string[];
  financeReconciledOrgNetRunRateUsd: number;
  attritionCohorts: readonly Readonly<{
    cohortId: string;
    eligibleCount: number;
    observedExitRateSamples: readonly number[];
    baselineExitRate: number;
    replacementAndRampCostUsd: number;
  }>[];
  capacityOrServiceLeakageUsd: number;
  calibrationDatasetId: string;
  reportingCutAsOf: string;
}>;

export type OrgPeopleRiskAdjustedNetEbitdaOutput = Readonly<{
  primaryMetricKey: 'people_risk_adjusted_org_net_run_rate_usd';
  primaryMetricValue: number;
  financeReconciledOrgNetRunRateUsd: number;
  expectedIncrementalAttritionCostUsd: number;
  capacityOrServiceLeakageUsd: number;
  riskAdjustedNetRunRateUsd: number;
  cohortIncrementalExitCostUsd: Readonly<Record<string, number>>;
  cohortIncrementalExitCostDistributionUsd: Readonly<Record<string, readonly number[]>>;
  aggregateIncrementalExitCostDistributionUsd: readonly number[];
}>;

export type OrgOptionDashboardIdentity = Readonly<{
  optionId: string;
  optionVersion: number;
  snapshotId: string;
  scopeId: string;
  currency: 'USD';
  horizonStart: string;
  horizonEnd: string;
}>;

export type OrgOptionsDecisionDashboardInput = Readonly<{
  substrateAnchor: OrgAnalysisSubstrateAnchor;
  evidenceRefIds: readonly string[];
  identity: OrgOptionDashboardIdentity;
  cells: readonly OrgOptionDashboardCell[];
  reportingCutAsOf: string;
}>;

export type OrgOptionDashboardCell = Readonly<{
  metricKey: string;
  metricValue: number;
  valueDomain: 'org_cost' | 'workforce' | 'capacity' | 'service' | 'schedule' | 'risk';
  optionId: string;
  optionVersion: number;
  snapshotId: string;
  scopeId: string;
  currency: 'USD';
  horizonStart: string;
  horizonEnd: string;
  formulaRef: string;
  evidenceRefId: string;
}>;

export type OrgOptionsDecisionDashboardOutput = Readonly<{
  primaryMetricKey: 'identity_consistent_dashboard_metric_count';
  primaryMetricValue: number;
  projectedMetricCount: number;
  metricKeys: readonly string[];
  identity: OrgOptionDashboardIdentity;
  cells: readonly OrgOptionDashboardCell[];
  additiveValueUsd: null;
}>;

export type OrgRecommendationOption = Readonly<{
  optionId: string;
  optionType: 'do_nothing' | 'delayering' | 'shared_services';
  hardGatesPass: boolean;
  axisScores: Readonly<Record<string, number>>;
}>;

export type OrgRecommendedOptionRationaleInput = Readonly<{
  substrateAnchor: OrgAnalysisSubstrateAnchor;
  evidenceRefIds: readonly string[];
  executiveRationale: string;
  tradeoffs: readonly string[];
  decisiveEvidenceRefIds: readonly string[];
  assumptions: readonly string[];
  dataGaps: readonly string[];
  consequenceOfDelay: string;
  ownerRoleId: string;
  controlPlan: string;
  options: readonly OrgRecommendationOption[];
  decisionPolicy: Readonly<{
    policyId: string;
    policyVersion: number;
    weights: Readonly<Record<string, number>>;
  }> | null;
  reportingCutAsOf: string;
}>;

export type OrgRecommendedOptionRationaleOutput = Readonly<{
  primaryMetricKey: 'recommended_option_policy_score';
  primaryMetricValue: number;
  recommendedOptionId: string | null;
  recommendedOptionScore: number | null;
  eligibleOptionCount: number;
  paretoOptionIds: readonly string[];
  rejectedOptionIds: readonly string[];
  policyId: string | null;
  policyVersion: number | null;
  executiveRationale: string;
  tradeoffs: readonly string[];
  decisiveEvidenceRefIds: readonly string[];
  assumptions: readonly string[];
  dataGaps: readonly string[];
  consequenceOfDelay: string;
  ownerRoleId: string;
  controlPlan: string;
}>;

export type OrgScenarioDistributionDefinition = Readonly<{
  riskDriverId: string;
  distributionType: 'empirical_weighted_draws';
  distributionParameters: Readonly<{
    sampleDrawIds: readonly string[];
    weightField: 'jointProbabilityWeight';
  }>;
  correlationGroupId: string;
}>;

export type OrgScenarioCorrelation = Readonly<{
  leftRiskDriverId: string;
  rightRiskDriverId: string;
  coefficient: number;
}>;

export type OrgScenarioDistributionCorrelationReceipt = Readonly<{
  receiptId: string;
  inputDigest: string;
  distributionDefinitions: readonly OrgScenarioDistributionDefinition[];
  correlations: readonly OrgScenarioCorrelation[];
  preCorrelatedDrawIds: readonly string[];
}>;

export type OrgExecutionRiskTimeToRealizeInput = Readonly<{
  substrateAnchor: OrgAnalysisSubstrateAnchor;
  evidenceRefIds: readonly string[];
  mode: 'calibrated_distribution' | 'deterministic_sensitivity';
  workPackages: readonly Readonly<{
    workPackageId: string;
    predecessorIds: readonly string[];
    durationDays: number;
  }>[];
  baseEligibleValueUsd: number;
  draws: readonly Readonly<{
    drawId: string;
    driverId: string;
    jointProbabilityWeight: number;
    durationMultiplierByWorkPackageId: Readonly<Record<string, number>>;
    valueErosionUsd: number;
  }>[];
  simulationSeed: number;
  iterationCount: number;
  algorithmVersion: string;
  correlationModelVersion: string;
  distributionCorrelationReceipt: OrgScenarioDistributionCorrelationReceipt;
  calibrationDatasetId: string | null;
  reportingCutAsOf: string;
}>;

export type OrgExecutionRiskTimeToRealizeOutput = Readonly<{
  primaryMetricKey: 'execution_risk_completion_day';
  primaryMetricValue: number;
  posture: 'calibrated_distribution' | 'deterministic_sensitivity';
  completionP50Day: number | null;
  completionP80Day: number | null;
  sensitivityDownsideCompletionDay: number | null;
  valueP20Usd: number | null;
  completionDayByDrawId: Readonly<Record<string, number>>;
  valueUsdByDrawId: Readonly<Record<string, number>>;
  driverSensitivityById: Readonly<
    Record<
      string,
      Readonly<{
        completionDayDelta: number;
        valueDownsideUsd: number;
      }>
    >
  >;
  simulationSeed: number;
  iterationCount: number;
  algorithmVersion: string;
  correlationModelVersion: string;
  distributionCorrelationReceipt: OrgScenarioDistributionCorrelationReceipt;
}>;

export type OrgRealizationConfidenceBandInput = Readonly<{
  substrateAnchor: OrgAnalysisSubstrateAnchor;
  evidenceRefIds: readonly string[];
  mode: 'calibrated_confidence' | 'scenario_range';
  draws: readonly Readonly<{
    drawId: string;
    jointProbabilityWeight: number;
    eligibleRunRateUsd: number;
    rampFractions: readonly number[];
    recurringLeakageUsdByMonth: readonly number[];
    oneTimeCashUsdByMonth: readonly number[];
  }>[];
  observedActualsUsdByMonth: readonly number[];
  simulationSeed: number;
  iterationCount: number;
  algorithmVersion: string;
  correlationModelVersion: string;
  distributionCorrelationReceipt: OrgScenarioDistributionCorrelationReceipt;
  calibrationDatasetId: string | null;
  reportingCutAsOf: string;
}>;

export type OrgRealizationConfidenceBandOutput = Readonly<{
  primaryMetricKey: 'realization_value_range_width_usd';
  primaryMetricValue: number;
  posture: 'calibrated_confidence' | 'scenario_range';
  p10Usd: number | null;
  p50Usd: number | null;
  p90Usd: number | null;
  scenarioLowUsd: number | null;
  scenarioBaseUsd: number | null;
  scenarioHighUsd: number | null;
  rangeWidthUsd: number;
  calibrationDatasetId: string | null;
  realizedValueByDrawId: Readonly<Record<string, number>>;
  calibration: Readonly<{
    observedMonthCount: number;
    baseMeanAbsoluteErrorUsd: number;
  }> | null;
  simulationSeed: number;
  iterationCount: number;
  algorithmVersion: string;
  correlationModelVersion: string;
  inputDrawIds: readonly string[];
  distributionCorrelationReceipt: OrgScenarioDistributionCorrelationReceipt;
}>;

export type OrgAnalysisScenarioRawSnapshot = Readonly<{
  substrateAnchor: OrgAnalysisSubstrateAnchor;
  reportingCutAsOf: string;
  evidenceBindings: readonly Readonly<{
    evidenceRefId: string;
    evidenceArtifactId: string;
    formulaRef: string;
    sourceSystemRef: string;
    positionCount?: number;
    fullyLoadedAnnualCostUsd?: number;
  }>[];
  formulaEvidenceRefIds: Readonly<Record<OrgAnalysisDistinctScenarioFormulaId, readonly string[]>>;
  timeToRealize: Omit<
    OrgTimeToRealizeInput,
    'substrateAnchor' | 'evidenceRefIds' | 'reportingCutAsOf'
  >;
  delayering: Omit<
    OrgDelayeringNetEbitdaScenarioInput,
    'substrateAnchor' | 'evidenceRefIds' | 'reportingCutAsOf'
  >;
  optionComparison: Omit<
    OrgCompareCostStructureOptionsInput,
    'substrateAnchor' | 'evidenceRefIds' | 'reportingCutAsOf'
  >;
  savingsGate: Omit<
    OrgSavingsWorkRedesignGateInput,
    'substrateAnchor' | 'evidenceRefIds' | 'reportingCutAsOf'
  >;
  peopleRisk: Omit<
    OrgPeopleRiskAdjustedNetEbitdaInput,
    'substrateAnchor' | 'evidenceRefIds' | 'reportingCutAsOf'
  >;
  dashboard: Omit<
    OrgOptionsDecisionDashboardInput,
    'substrateAnchor' | 'evidenceRefIds' | 'reportingCutAsOf'
  >;
  recommendation: Omit<
    OrgRecommendedOptionRationaleInput,
    'substrateAnchor' | 'evidenceRefIds' | 'reportingCutAsOf'
  >;
  riskModel: Readonly<{
    executionMode: 'calibrated_distribution' | 'deterministic_sensitivity';
    confidenceMode: 'calibrated_confidence' | 'scenario_range';
    baseEligibleValueUsd: number;
    draws: readonly Readonly<{
      drawId: string;
      driverId: string;
      jointProbabilityWeight: number;
      durationMultiplierByWorkPackageId: Readonly<Record<string, number>>;
      valueErosionUsd: number;
      eligibleRunRateUsd: number;
      rampFractions: readonly number[];
      recurringLeakageUsdByMonth: readonly number[];
      oneTimeCashUsdByMonth: readonly number[];
    }>[];
    simulationSeed: number;
    algorithmVersion: string;
    correlationModelVersion: string;
    distributionCorrelationReceipt: Omit<OrgScenarioDistributionCorrelationReceipt, 'inputDigest'>;
    executionCalibrationDatasetId: string | null;
    realizationCalibrationDatasetId: string | null;
    observedActualsUsdByMonth: readonly number[];
  }>;
}>;

export type OrgAnalysisDistinctScenarioFormulaInputs = Readonly<{
  org_time_to_realize_v1: OrgTimeToRealizeInput;
  org_delayering_net_ebitda_scenario_v1: OrgDelayeringNetEbitdaScenarioInput;
  org_compare_cost_structure_options_v1: OrgCompareCostStructureOptionsInput;
  org_savings_work_redesign_gate_v1: OrgSavingsWorkRedesignGateInput;
  org_people_risk_adjusted_net_ebitda_v1: OrgPeopleRiskAdjustedNetEbitdaInput;
  org_options_decision_dashboard_v1: OrgOptionsDecisionDashboardInput;
  org_recommended_option_rationale_v1: OrgRecommendedOptionRationaleInput;
  org_execution_risk_time_to_realize_v1: OrgExecutionRiskTimeToRealizeInput;
  org_realization_confidence_band_v1: OrgRealizationConfidenceBandInput;
}>;

export function compileOrgAnalysisScenarioFormulaInputs(
  snapshot: OrgAnalysisScenarioRawSnapshot
): OrgAnalysisDistinctScenarioFormulaInputs {
  const commonFor = (formulaId: OrgAnalysisDistinctScenarioFormulaId) => ({
    substrateAnchor: snapshot.substrateAnchor,
    evidenceRefIds: snapshot.formulaEvidenceRefIds[formulaId],
    reportingCutAsOf: snapshot.reportingCutAsOf,
  });
  const executionDraws = snapshot.riskModel.draws.map(
    ({
      drawId,
      driverId,
      jointProbabilityWeight,
      durationMultiplierByWorkPackageId,
      valueErosionUsd,
    }) => ({
      drawId,
      driverId,
      jointProbabilityWeight,
      durationMultiplierByWorkPackageId,
      valueErosionUsd,
    })
  );
  const confidenceDraws = snapshot.riskModel.draws.map(
    ({
      drawId,
      jointProbabilityWeight,
      eligibleRunRateUsd,
      rampFractions,
      recurringLeakageUsdByMonth,
      oneTimeCashUsdByMonth,
    }) => ({
      drawId,
      jointProbabilityWeight,
      eligibleRunRateUsd,
      rampFractions,
      recurringLeakageUsdByMonth,
      oneTimeCashUsdByMonth,
    })
  );
  const distributionCorrelationReceiptFor = (draws: readonly unknown[]) =>
    Object.freeze({
      ...snapshot.riskModel.distributionCorrelationReceipt,
      inputDigest: scenarioDistributionCorrelationDigest({
        draws,
        simulationSeed: snapshot.riskModel.simulationSeed,
        iterationCount: snapshot.riskModel.draws.length,
        algorithmVersion: snapshot.riskModel.algorithmVersion,
        correlationModelVersion: snapshot.riskModel.correlationModelVersion,
        receipt: snapshot.riskModel.distributionCorrelationReceipt,
      }),
    });
  return Object.freeze({
    org_time_to_realize_v1: Object.freeze({
      ...commonFor('org_time_to_realize_v1'),
      ...snapshot.timeToRealize,
    }),
    org_delayering_net_ebitda_scenario_v1: Object.freeze({
      ...commonFor('org_delayering_net_ebitda_scenario_v1'),
      ...snapshot.delayering,
    }),
    org_compare_cost_structure_options_v1: Object.freeze({
      ...commonFor('org_compare_cost_structure_options_v1'),
      ...snapshot.optionComparison,
    }),
    org_savings_work_redesign_gate_v1: Object.freeze({
      ...commonFor('org_savings_work_redesign_gate_v1'),
      ...snapshot.savingsGate,
    }),
    org_people_risk_adjusted_net_ebitda_v1: Object.freeze({
      ...commonFor('org_people_risk_adjusted_net_ebitda_v1'),
      ...snapshot.peopleRisk,
    }),
    org_options_decision_dashboard_v1: Object.freeze({
      ...commonFor('org_options_decision_dashboard_v1'),
      ...snapshot.dashboard,
    }),
    org_recommended_option_rationale_v1: Object.freeze({
      ...commonFor('org_recommended_option_rationale_v1'),
      ...snapshot.recommendation,
    }),
    org_execution_risk_time_to_realize_v1: Object.freeze({
      ...commonFor('org_execution_risk_time_to_realize_v1'),
      mode: snapshot.riskModel.executionMode,
      workPackages: snapshot.timeToRealize.workPackages.map(
        ({ workPackageId, predecessorIds, durationDays }) => ({
          workPackageId,
          predecessorIds,
          durationDays,
        })
      ),
      baseEligibleValueUsd: snapshot.riskModel.baseEligibleValueUsd,
      draws: executionDraws,
      simulationSeed: snapshot.riskModel.simulationSeed,
      iterationCount: snapshot.riskModel.draws.length,
      algorithmVersion: snapshot.riskModel.algorithmVersion,
      correlationModelVersion: snapshot.riskModel.correlationModelVersion,
      distributionCorrelationReceipt: distributionCorrelationReceiptFor(executionDraws),
      calibrationDatasetId: snapshot.riskModel.executionCalibrationDatasetId,
    }),
    org_realization_confidence_band_v1: Object.freeze({
      ...commonFor('org_realization_confidence_band_v1'),
      mode: snapshot.riskModel.confidenceMode,
      draws: confidenceDraws,
      observedActualsUsdByMonth: snapshot.riskModel.observedActualsUsdByMonth,
      simulationSeed: snapshot.riskModel.simulationSeed,
      iterationCount: snapshot.riskModel.draws.length,
      algorithmVersion: snapshot.riskModel.algorithmVersion,
      correlationModelVersion: snapshot.riskModel.correlationModelVersion,
      distributionCorrelationReceipt: distributionCorrelationReceiptFor(confidenceDraws),
      calibrationDatasetId: snapshot.riskModel.realizationCalibrationDatasetId,
    }),
  });
}

function blocked<TOutput>(
  reasonCode: 'invalid_formula_input' | 'missing_formula_input' | 'never_blend_violation',
  message: string
): ApprovedFormulaResult<TOutput> {
  return { status: 'blocked', reasonCode, message };
}

function ok<TOutput>(output: TOutput): ApprovedFormulaResult<TOutput> {
  return { status: 'ok', output: Object.freeze(output) };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isFiniteNonNegative(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function hasValidSubstrateAnchor(
  input:
    | {
        substrateAnchor?: OrgAnalysisSubstrateAnchor;
        evidenceRefIds?: readonly string[];
        reportingCutAsOf?: string;
      }
    | null
    | undefined
): boolean {
  const anchor = input?.substrateAnchor;
  return Boolean(
    anchor &&
    isNonEmptyString(anchor.tenantId) &&
    isNonEmptyString(anchor.orgStateId) &&
    /^\d{4}-\d{2}-\d{2}$/.test(anchor.reportingCutAsOf) &&
    anchor.reportingCutAsOf === input?.reportingCutAsOf &&
    Number.isInteger(anchor.positionCount) &&
    anchor.positionCount > 0 &&
    isFiniteNonNegative(anchor.fullyLoadedAnnualCostUsd) &&
    anchor.fullyLoadedAnnualCostUsd > 0 &&
    isNonEmptyString(anchor.evidenceRefId) &&
    anchor.formulaRef === 'org_position_cost_rollup_v1@1.0.0' &&
    isNonEmptyString(anchor.oracleReceiptId) &&
    Array.isArray(input?.evidenceRefIds) &&
    input.evidenceRefIds.length > 0 &&
    new Set(input.evidenceRefIds).size === input.evidenceRefIds.length &&
    input.evidenceRefIds.every((evidenceRefId) => isNonEmptyString(evidenceRefId)) &&
    input.evidenceRefIds.includes(anchor.evidenceRefId) &&
    (anchor.oracleRung === 'R2' || anchor.oracleRung === 'R3')
  );
}

function roundUsd(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function roundMetric(value: number): number {
  return Math.round((value + Number.EPSILON) * 1_000_000) / 1_000_000;
}

function canonicalJson(value: unknown): string {
  return JSON.stringify(canonicalValue(value));
}

function canonicalValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalValue);
  if (!isRecord(value)) return value;
  return Object.fromEntries(
    Object.entries(value)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, child]) => [key, canonicalValue(child)])
  );
}

function fnv1a32(value: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return `fnv1a32:${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

function scenarioDistributionCorrelationDigest(
  input: Readonly<{
    draws: readonly unknown[];
    simulationSeed: number;
    iterationCount: number;
    algorithmVersion: string;
    correlationModelVersion: string;
    receipt: Omit<OrgScenarioDistributionCorrelationReceipt, 'inputDigest'>;
  }>
): string {
  return fnv1a32(canonicalJson(input));
}

function hasValidDistributionCorrelationReceipt(
  input: OrgExecutionRiskTimeToRealizeInput | OrgRealizationConfidenceBandInput
): boolean {
  const receipt = input.distributionCorrelationReceipt;
  if (
    !receipt ||
    !isNonEmptyString(receipt.receiptId) ||
    !/^fnv1a32:[0-9a-f]{8}$/.test(receipt.inputDigest) ||
    !Array.isArray(receipt.distributionDefinitions) ||
    receipt.distributionDefinitions.length === 0 ||
    !Array.isArray(receipt.correlations) ||
    receipt.correlations.length === 0 ||
    !Array.isArray(receipt.preCorrelatedDrawIds)
  ) {
    return false;
  }
  const drawIds = input.draws.map((draw) => draw.drawId).sort();
  const receiptDrawIds = [...receipt.preCorrelatedDrawIds].sort();
  if (
    drawIds.length !== receiptDrawIds.length ||
    drawIds.some((drawId, index) => drawId !== receiptDrawIds[index]) ||
    input.draws.some(
      (draw) =>
        !isNonEmptyString(draw.drawId) ||
        !Number.isFinite(draw.jointProbabilityWeight) ||
        draw.jointProbabilityWeight <= 0
    )
  ) {
    return false;
  }
  const driverIds = new Set(
    receipt.distributionDefinitions.map((definition) => definition.riskDriverId)
  );
  if (
    driverIds.size !== receipt.distributionDefinitions.length ||
    receipt.distributionDefinitions.some(
      (definition) =>
        !isNonEmptyString(definition.riskDriverId) ||
        definition.distributionType !== 'empirical_weighted_draws' ||
        !isNonEmptyString(definition.correlationGroupId) ||
        definition.distributionParameters?.weightField !== 'jointProbabilityWeight' ||
        !Array.isArray(definition.distributionParameters?.sampleDrawIds) ||
        definition.distributionParameters.sampleDrawIds.length === 0 ||
        definition.distributionParameters.sampleDrawIds.some(
          (drawId: string) => !receiptDrawIds.includes(drawId)
        )
    )
  ) {
    return false;
  }
  const correlationKeys = new Set<string>();
  for (const correlation of receipt.correlations) {
    const key = [correlation.leftRiskDriverId, correlation.rightRiskDriverId].sort().join(':');
    if (
      !driverIds.has(correlation.leftRiskDriverId) ||
      !driverIds.has(correlation.rightRiskDriverId) ||
      correlation.leftRiskDriverId === correlation.rightRiskDriverId ||
      !Number.isFinite(correlation.coefficient) ||
      correlation.coefficient < -1 ||
      correlation.coefficient > 1 ||
      correlationKeys.has(key)
    ) {
      return false;
    }
    correlationKeys.add(key);
  }
  if ('driverId' in input.draws[0]) {
    const executionDraws = input.draws as OrgExecutionRiskTimeToRealizeInput['draws'];
    if (executionDraws.some((draw) => draw.driverId !== 'base' && !driverIds.has(draw.driverId))) {
      return false;
    }
  }
  const { inputDigest: _inputDigest, ...receiptWithoutDigest } = receipt;
  return (
    receipt.inputDigest ===
    scenarioDistributionCorrelationDigest({
      draws: input.draws,
      simulationSeed: input.simulationSeed,
      iterationCount: input.iterationCount,
      algorithmVersion: input.algorithmVersion,
      correlationModelVersion: input.correlationModelVersion,
      receipt: receiptWithoutDigest,
    })
  );
}

function weightedPercentile(
  samples: readonly Readonly<{ value: number; weight: number }>[],
  quantile: number
): number {
  const ordered = [...samples].sort((left, right) => left.value - right.value);
  const totalWeight = ordered.reduce((sum, sample) => sum + sample.weight, 0);
  const threshold = quantile * totalWeight;
  let cumulativeWeight = 0;
  for (const sample of ordered) {
    cumulativeWeight += sample.weight;
    if (cumulativeWeight >= threshold) return sample.value;
  }
  return ordered[ordered.length - 1].value;
}

function validateSharedScenarioEvidenceBindings(
  formulaId: OrgAnalysisDistinctScenarioFormulaId,
  input: unknown,
  snapshot: OrgAnalysisScenarioRawSnapshot
): ApprovedFormulaEvidenceValidationBlock | null {
  const formulaInput = isRecord(input) ? input : null;
  const evidenceRefIds = Array.isArray(formulaInput?.evidenceRefIds)
    ? formulaInput.evidenceRefIds
    : [];
  const bindingByRefId = new Map(
    snapshot.evidenceBindings.map((binding) => [binding.evidenceRefId, binding])
  );
  if (
    !formulaInput ||
    bindingByRefId.size !== snapshot.evidenceBindings.length ||
    evidenceRefIds.length === 0 ||
    evidenceRefIds.some(
      (evidenceRefId) => !isNonEmptyString(evidenceRefId) || !bindingByRefId.has(evidenceRefId)
    )
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message: `${formulaId} contains an unresolved scenario evidence reference.`,
    };
  }
  const anchor = formulaInput?.substrateAnchor as OrgAnalysisSubstrateAnchor | undefined;
  const anchorBinding = anchor ? bindingByRefId.get(anchor.evidenceRefId) : undefined;
  if (
    !anchor ||
    !anchorBinding ||
    anchorBinding.formulaRef !== anchor.formulaRef ||
    anchorBinding.positionCount !== anchor.positionCount ||
    anchorBinding.fullyLoadedAnnualCostUsd !== anchor.fullyLoadedAnnualCostUsd ||
    !isNonEmptyString(anchorBinding.evidenceArtifactId) ||
    !isNonEmptyString(anchorBinding.sourceSystemRef)
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message: `${formulaId} substrate anchor does not reconcile to a governed position-cost receipt.`,
    };
  }
  if (formulaId === 'org_options_decision_dashboard_v1') {
    const cells = Array.isArray(formulaInput.cells) ? formulaInput.cells : [];
    for (const rawCell of cells) {
      const cell = isRecord(rawCell) ? rawCell : null;
      const binding = isNonEmptyString(cell?.evidenceRefId)
        ? bindingByRefId.get(cell.evidenceRefId)
        : undefined;
      if (!binding || binding.formulaRef !== cell?.formulaRef) {
        return {
          reasonCode: 'formula_reconciliation_failure',
          message: 'Dashboard cell formula and evidence references do not reconcile.',
        };
      }
    }
  }
  if (formulaId === 'org_recommended_option_rationale_v1') {
    const decisiveRefs = Array.isArray(formulaInput.decisiveEvidenceRefIds)
      ? formulaInput.decisiveEvidenceRefIds
      : [];
    if (
      decisiveRefs.length === 0 ||
      decisiveRefs.some(
        (evidenceRefId) => !isNonEmptyString(evidenceRefId) || !bindingByRefId.has(evidenceRefId)
      )
    ) {
      return {
        reasonCode: 'formula_reconciliation_failure',
        message: 'Recommendation contains an unresolved decisive evidence reference.',
      };
    }
  }
  return null;
}

function validateEvidenceInput(
  formulaId: string,
  input: unknown,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[]
): ApprovedFormulaEvidenceValidationBlock | null {
  for (const artifact of artifacts) {
    const content = isRecord(artifact.contentJson) ? artifact.contentJson : null;
    if (
      ORG_ANALYSIS_DISTINCT_SCENARIO_FORMULA_IDS.includes(
        formulaId as OrgAnalysisDistinctScenarioFormulaId
      )
    ) {
      const rawSnapshot = content?.orgAnalysisScenarioRawSnapshot;
      if (!isRecord(rawSnapshot)) continue;
      const rawAnchor = isRecord(rawSnapshot.substrateAnchor) ? rawSnapshot.substrateAnchor : null;
      if (
        !rawAnchor ||
        rawAnchor.tenantId !== content?.tenantId ||
        rawAnchor.orgStateId !== content?.orgStateId ||
        rawAnchor.reportingCutAsOf !== content?.reportingCutAsOf
      ) {
        return {
          reasonCode: 'formula_reconciliation_failure',
          message: `${formulaId} substrate anchor does not match the governed artifact scope.`,
        };
      }
      try {
        const compiled = compileOrgAnalysisScenarioFormulaInputs(
          rawSnapshot as OrgAnalysisScenarioRawSnapshot
        );
        const distinctFormulaId = formulaId as OrgAnalysisDistinctScenarioFormulaId;
        if (canonicalJson(compiled[distinctFormulaId]) !== canonicalJson(input)) {
          return {
            reasonCode: 'formula_reconciliation_failure',
            message: `${formulaId} input does not reconcile to the shared governed org-analysis scenario snapshot.`,
          };
        }
        return validateSharedScenarioEvidenceBindings(
          distinctFormulaId,
          input,
          rawSnapshot as OrgAnalysisScenarioRawSnapshot
        );
      } catch {
        return {
          reasonCode: 'formula_reconciliation_failure',
          message: `${formulaId} shared governed org-analysis scenario snapshot is invalid.`,
        };
      }
    }
    const formulaInputs = isRecord(content?.orgAnalysisFormulaInputs)
      ? content.orgAnalysisFormulaInputs
      : null;
    if (!formulaInputs || !(formulaId in formulaInputs)) continue;
    return canonicalJson(formulaInputs[formulaId]) === canonicalJson(input)
      ? null
      : {
          reasonCode: 'formula_reconciliation_failure',
          message: `${formulaId} input does not reconcile to governed org-analysis evidence.`,
        };
  }
  return {
    reasonCode: 'missing_governed_evidence',
    message: `${formulaId} requires a matching governed org-analysis evidence input.`,
  };
}

function implementationDigestInput(
  name: string,
  implementation: unknown
): ApprovedFormulaImplementationDigestInput {
  return Object.freeze({ name, source: String(implementation) });
}

function createDefinition<TInput, TOutput>(input: {
  formulaId: string;
  displayName: string;
  description: string;
  inputSchemaVersion: string;
  outputSchemaVersion: string;
  execute: (formulaInput: TInput) => ApprovedFormulaResult<TOutput>;
  goldenTraces: readonly ApprovedFormulaGoldenTrace<TInput, TOutput>[];
  helpers?: readonly Readonly<[string, unknown]>[];
}): ApprovedFormulaDefinition<TInput, TOutput> {
  const reconciliationRule: ApprovedFormulaReconciliationRule<TInput, TOutput> = Object.freeze({
    kind: 'input_output_evidence_reconciliation',
    ruleId: `${input.formulaId}_reconciles_to_evidence_v1`,
    description:
      'Formula output must equal a deterministic replay of the exact governed org-analysis input.',
    validate(formulaInput, output, artifacts) {
      const evidenceBlock = validateEvidenceInput(input.formulaId, formulaInput, artifacts);
      if (evidenceBlock) return evidenceBlock;
      const replay = input.execute(formulaInput as TInput);
      if (replay.status === 'blocked' || canonicalJson(replay.output) !== canonicalJson(output)) {
        return {
          reasonCode: 'formula_reconciliation_failure',
          message: `${input.formulaId} output failed deterministic replay reconciliation.`,
        };
      }
      return null;
    },
  });
  const digestInputs = [
    implementationDigestInput(input.execute.name || input.formulaId, input.execute),
    implementationDigestInput('validateEvidenceInput', validateEvidenceInput),
    implementationDigestInput('canonicalJson', canonicalJson),
    implementationDigestInput(
      'compileOrgAnalysisScenarioFormulaInputs',
      compileOrgAnalysisScenarioFormulaInputs
    ),
    implementationDigestInput('hasValidSubstrateAnchor', hasValidSubstrateAnchor),
    ...(input.helpers ?? []).map(([name, helper]) => implementationDigestInput(name, helper)),
  ];
  return Object.freeze({
    formulaId: input.formulaId,
    version: ORG_ANALYSIS_FORMULA_VERSION,
    displayName: input.displayName,
    description: input.description,
    lifecycle: 'code_defined',
    formulaRuntimeVersion: FORMULA_RUNTIME_VERSION,
    canonicalizerVersion: CANONICALIZER_VERSION,
    inputSchemaVersion: input.inputSchemaVersion,
    outputSchemaVersion: input.outputSchemaVersion,
    expectedContextKind: ORG_ANALYSIS_CONTEXT_KIND,
    approvedSourceSystemRefs: Object.freeze([
      ORG_COST_OPTIMIZATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
    ]),
    implementationDigestInputs: Object.freeze(digestInputs),
    goldenTraces: Object.freeze(input.goldenTraces.map((trace) => Object.freeze(trace))),
    validateInputAgainstEvidence(formulaInput, artifacts) {
      return validateEvidenceInput(input.formulaId, formulaInput, artifacts);
    },
    reconciliationRule,
    execute: input.execute,
  });
}

function executeOrgPositionCostRollup(
  input: OrgPositionCostRollupInput
): ApprovedFormulaResult<OrgPositionCostRollupOutput> {
  if (
    !input ||
    !Array.isArray(input.positionIds) ||
    input.positionIds.length === 0 ||
    !Array.isArray(input.costRows) ||
    !isNonEmptyString(input.reportingCutAsOf)
  ) {
    return blocked(
      'missing_formula_input',
      'Org position-cost rollup requires positions, cost rows, and reporting cut.'
    );
  }
  const positionIds = new Set(input.positionIds);
  if (
    positionIds.size !== input.positionIds.length ||
    [...positionIds].some((id) => !isNonEmptyString(id))
  ) {
    return blocked(
      'invalid_formula_input',
      'Org position-cost rollup requires unique non-empty position ids.'
    );
  }
  const costsByPositionId = new Map<string, number>();
  for (const row of input.costRows) {
    if (
      !positionIds.has(row.positionId) ||
      costsByPositionId.has(row.positionId) ||
      row.currency !== 'USD' ||
      !isFiniteNonNegative(row.fullyLoadedAnnualCostUsd)
    ) {
      return blocked(
        'invalid_formula_input',
        'Org position-cost rows must be unique, in-scope, finite non-negative USD values.'
      );
    }
    costsByPositionId.set(row.positionId, row.fullyLoadedAnnualCostUsd);
  }
  const missingCostPositionIds = input.positionIds.filter(
    (positionId) => !costsByPositionId.has(positionId)
  );
  if (missingCostPositionIds.length > 0) {
    return blocked(
      'missing_formula_input',
      `Org position-cost coverage is incomplete for ${missingCostPositionIds.join(', ')}.`
    );
  }
  const total = input.positionIds.reduce(
    (sum, positionId) => sum + (costsByPositionId.get(positionId) ?? 0),
    0
  );
  return ok({
    primaryMetricKey: 'fully_loaded_org_cost_usd',
    primaryMetricValue: roundUsd(total),
    positionCount: input.positionIds.length,
    fullyLoadedAnnualCostUsd: roundUsd(total),
    missingCostPositionIds: Object.freeze([]),
  });
}

/**
 * Net run-rate operating-income contribution of the governed org-cost sub-lever
 * buildup: per sub-lever, gross annual run-rate cost reduction minus the one-time
 * cost to achieve it; summed across the buildup.
 *
 *   net(i)  = grossRunRateCostReductionUsd(i) - oneTimeCostUsd(i)
 *   total   = SUM over i of net(i)
 *
 * Unit USD, temporal type flow, sign convention `positive_improves_metric`
 * (a positive total raises operating income). This is the same arithmetic the
 * org-analysis module has always performed over the buildup evidence and the same
 * arithmetic the independent oracle recomputes from plan source rows
 * (`annualBenefitUsd - estimatedDeliverySpendUsdP80` over non-workforce
 * cost-savings initiatives) — promoted here to a citable approved formula.
 */
function executeOrgCostSubLeverNetRunRateContribution(
  input: OrgCostSubLeverNetRunRateContributionInput
): ApprovedFormulaResult<OrgCostSubLeverNetRunRateContributionOutput> {
  if (
    !input ||
    !isNonEmptyString(input.orgStateId) ||
    !isNonEmptyString(input.reportingCutAsOf) ||
    !Array.isArray(input.contributions) ||
    input.contributions.length === 0
  ) {
    return blocked(
      'missing_formula_input',
      'Org-cost sub-lever contribution requires an org state, a reporting cut, and at least one governed sub-lever row.'
    );
  }
  const seen = new Set<string>();
  for (const row of input.contributions) {
    if (
      !row ||
      !isNonEmptyString(row.subLeverId) ||
      seen.has(row.subLeverId) ||
      row.currency !== 'USD' ||
      !isFiniteNonNegative(row.grossRunRateCostReductionUsd) ||
      !isFiniteNonNegative(row.oneTimeCostUsd)
    ) {
      return blocked(
        'invalid_formula_input',
        'Org-cost sub-lever rows must carry unique ids and finite non-negative USD gross and one-time amounts.'
      );
    }
    seen.add(row.subLeverId);
  }
  const workforceSubLeverIds = input.contributions
    .filter((row) => row.deltaHeadcount !== 0)
    .map((row) => row.subLeverId);
  if (workforceSubLeverIds.length > 0) {
    return blocked(
      'never_blend_violation',
      `Org-cost sub-levers must not move headcount; workforce levers are a separate claim: ${workforceSubLeverIds.join(', ')}.`
    );
  }
  const grossRunRateCostReductionUsd = roundUsd(
    input.contributions.reduce((sum, row) => sum + row.grossRunRateCostReductionUsd, 0)
  );
  const oneTimeCostUsd = roundUsd(
    input.contributions.reduce((sum, row) => sum + row.oneTimeCostUsd, 0)
  );
  const total = roundUsd(
    input.contributions.reduce(
      (sum, row) => sum + roundUsd(row.grossRunRateCostReductionUsd - row.oneTimeCostUsd),
      0
    )
  );
  return ok({
    primaryMetricKey: 'org_cost_sub_lever_net_run_rate_contribution_usd',
    primaryMetricValue: total,
    subLeverCount: input.contributions.length,
    grossRunRateCostReductionUsd,
    oneTimeCostUsd,
    orgCostSubLeverNetRunRateContributionUsd: total,
    subLeverIds: Object.freeze(input.contributions.map((row) => row.subLeverId)),
  });
}

function executeOrgTimeToRealize(
  input: OrgTimeToRealizeInput
): ApprovedFormulaResult<OrgTimeToRealizeOutput> {
  if (
    !input ||
    !hasValidSubstrateAnchor(input) ||
    !isNonEmptyString(input.optionId) ||
    !Number.isInteger(input.optionVersion) ||
    input.optionVersion <= 0 ||
    !Array.isArray(input.workPackages) ||
    input.workPackages.length === 0
  ) {
    return blocked(
      'missing_formula_input',
      'Time-to-realize requires one option version and an integrated schedule.'
    );
  }
  const byId = new Map(input.workPackages.map((row) => [row.workPackageId, row]));
  if (byId.size !== input.workPackages.length) {
    return blocked('invalid_formula_input', 'Time-to-realize work package ids must be unique.');
  }
  for (const row of input.workPackages) {
    if (
      !isNonEmptyString(row.workPackageId) ||
      !isNonEmptyString(row.ownerRoleId) ||
      !Number.isInteger(row.durationDays) ||
      row.durationDays <= 0 ||
      (row.cumulativeRunRateFraction !== null &&
        (!isFiniteNonNegative(row.cumulativeRunRateFraction) ||
          row.cumulativeRunRateFraction > 1)) ||
      row.predecessorIds.some((id: string) => !byId.has(id) || id === row.workPackageId)
    ) {
      return blocked(
        'invalid_formula_input',
        'Time-to-realize requires positive durations, owners, and valid predecessor ids.'
      );
    }
  }
  const schedule = buildOrgTimeToRealizeSchedule(input.workPackages);
  if (!schedule)
    return blocked('invalid_formula_input', 'Time-to-realize schedule contains a dependency cycle.');
  const benefitRows = input.workPackages.filter((row) => row.startsBenefit);
  const fullRunRateRows = input.workPackages.filter((row) => row.requiredForFullRunRate);
  if (benefitRows.length === 0 || fullRunRateRows.length === 0) {
    return blocked(
      'missing_formula_input',
      'Time-to-realize requires first-benefit and full-run-rate milestones.'
    );
  }
  const firstBenefitDay = Math.min(
    ...benefitRows.map((row) => schedule.completionDays.get(row.workPackageId) ?? 0)
  );
  const fullRunRateRow = [...fullRunRateRows].sort(
    (left, right) =>
      (schedule.completionDays.get(right.workPackageId) ?? 0) -
      (schedule.completionDays.get(left.workPackageId) ?? 0)
  )[0];
  const fullRunRateDay = schedule.completionDays.get(fullRunRateRow.workPackageId) ?? 0;
  const realizationCurve = input.workPackages
    .filter((row) => row.cumulativeRunRateFraction !== null && row.cumulativeRunRateFraction > 0)
    .map((row) => ({
      workPackageId: row.workPackageId,
      completionDay: schedule.completionDays.get(row.workPackageId) ?? 0,
      cumulativeRunRateFraction: row.cumulativeRunRateFraction as number,
    }))
    .sort(
      (left, right) =>
        left.completionDay - right.completionDay ||
        left.workPackageId.localeCompare(right.workPackageId)
    );
  if (
    realizationCurve.length === 0 ||
    realizationCurve[realizationCurve.length - 1].cumulativeRunRateFraction !== 1 ||
    realizationCurve.some(
      (point, index) =>
        index > 0 &&
        point.cumulativeRunRateFraction < realizationCurve[index - 1].cumulativeRunRateFraction
    )
  ) {
    return blocked(
      'invalid_formula_input',
      'Time-to-realize requires a monotonic realization curve ending at full run rate.'
    );
  }
  return ok({
    primaryMetricKey: 'full_run_rate_day',
    primaryMetricValue: fullRunRateDay,
    firstBenefitDay,
    fullRunRateDay,
    criticalPathWorkPackageIds:
      schedule.criticalPathByWorkPackageId.get(fullRunRateRow.workPackageId) ?? [],
    earliestFinishDayByWorkPackageId: Object.freeze(Object.fromEntries(schedule.completionDays)),
    realizationCurve: Object.freeze(realizationCurve.map((point) => Object.freeze(point))),
    scheduleGapCodes: Object.freeze([]),
  });
}

function graphMetrics(
  positions: readonly OrgDelayeringPosition[]
): Readonly<{ managerCount: number; maxDepth: number }> | null {
  const byId = new Map(positions.map((position) => [position.positionId, position]));
  if (
    byId.size !== positions.length ||
    positions.length === 0 ||
    positions.filter((position) => position.managerPositionId === null).length !== 1 ||
    positions.some(
      (position) =>
        !isNonEmptyString(position.positionId) ||
        !isFiniteNonNegative(position.fullyLoadedAnnualCostUsd) ||
        (position.managerPositionId !== null &&
          (!byId.has(position.managerPositionId) ||
            position.managerPositionId === position.positionId)) ||
        new Set(position.assignedWorkPackageIds).size !== position.assignedWorkPackageIds.length ||
        position.assignedWorkPackageIds.some((id) => !isNonEmptyString(id))
    )
  ) {
    return null;
  }
  const depths = new Map<string, number>();
  const visiting = new Set<string>();
  const resolveDepth = (positionId: string): number | null => {
    const known = depths.get(positionId);
    if (known !== undefined) return known;
    if (visiting.has(positionId)) return null;
    visiting.add(positionId);
    const position = byId.get(positionId);
    if (!position) return null;
    const depth =
      position.managerPositionId === null ? 1 : resolveDepth(position.managerPositionId);
    if (depth === null) return null;
    const resolved = position.managerPositionId === null ? depth : depth + 1;
    visiting.delete(positionId);
    depths.set(positionId, resolved);
    return resolved;
  };
  for (const positionId of byId.keys()) {
    if (resolveDepth(positionId) === null) return null;
  }
  const managerIds = new Set(
    positions
      .map((position) => position.managerPositionId)
      .filter((id): id is string => id !== null)
  );
  return Object.freeze({
    managerCount: managerIds.size,
    maxDepth: Math.max(...depths.values()),
  });
}

function executeOrgDelayeringNetEbitdaScenario(
  input: OrgDelayeringNetEbitdaScenarioInput
): ApprovedFormulaResult<OrgDelayeringNetEbitdaScenarioOutput> {
  if (
    !input ||
    !hasValidSubstrateAnchor(input) ||
    input.optionType !== 'delayering' ||
    input.valueDomain !== 'org_cost' ||
    !isNonEmptyString(input.optionId) ||
    !Number.isInteger(input.optionVersion) ||
    input.optionVersion <= 0 ||
    !Array.isArray(input.baselinePositions) ||
    !Array.isArray(input.actions) ||
    input.actions.length === 0 ||
    !isFiniteNonNegative(input.recurringLeakageUsd) ||
    !Number.isFinite(input.discountRateAnnual) ||
    input.discountRateAnnual < 0 ||
    !Array.isArray(input.monthlyNetCashUsd) ||
    input.monthlyNetCashUsd.length === 0 ||
    input.monthlyNetCashUsd.some((value) => !Number.isFinite(value)) ||
    !Array.isArray(input.oneTimeCashUsdByMonth) ||
    input.oneTimeCashUsdByMonth.length !== input.monthlyNetCashUsd.length ||
    input.oneTimeCashUsdByMonth.some((value) => !isFiniteNonNegative(value)) ||
    !Array.isArray(input.capacityRows) ||
    input.capacityRows.length === 0 ||
    input.capacityRows.some(
      (row) =>
        !isNonEmptyString(row.workPackageId) ||
        !isFiniteNonNegative(row.requiredFte) ||
        !isFiniteNonNegative(row.assignedFte)
    ) ||
    !Array.isArray(input.serviceOutcomeRows) ||
    input.serviceOutcomeRows.length === 0 ||
    input.serviceOutcomeRows.some(
      (row) =>
        !isNonEmptyString(row.metricId) ||
        !Number.isFinite(row.baselineValue) ||
        !Number.isFinite(row.scenarioValue) ||
        (row.direction !== 'higher_is_better' && row.direction !== 'lower_is_better')
    ) ||
    !isNonEmptyString(input.financeDefinitionRef) ||
    !Array.isArray(input.mappedGlAccountIds) ||
    input.mappedGlAccountIds.length === 0 ||
    new Set(input.mappedGlAccountIds).size !== input.mappedGlAccountIds.length ||
    input.mappedGlAccountIds.some((accountId) => !isNonEmptyString(accountId)) ||
    input.financeReconciled !== true
  ) {
    return blocked(
      'invalid_formula_input',
      'Delayering scenario requires a governed target graph, explicit actions, separated economics, and monthly cash flow.'
    );
  }
  const beforeMetrics = graphMetrics(input.baselinePositions);
  const actionIds = new Set(input.actions.map((action) => action.actionId));
  if (
    !beforeMetrics ||
    actionIds.size !== input.actions.length ||
    input.actions.some(
      (action) => !isNonEmptyString(action.actionId) || !isNonEmptyString(action.positionId)
    )
  ) {
    return blocked(
      'invalid_formula_input',
      'Delayering baseline graph and action identities must be complete, unique, and acyclic.'
    );
  }
  const beforeById = new Map(
    input.baselinePositions.map((position) => [position.positionId, position])
  );
  const afterById = new Map<string, OrgDelayeringPosition>(
    input.baselinePositions.map((position) => [
      position.positionId,
      {
        ...position,
        assignedWorkPackageIds: [...position.assignedWorkPackageIds],
      },
    ])
  );
  const removedPositionIds: string[] = [];
  const addedPositionIds: string[] = [];
  const reparentedPositionIds: string[] = [];
  const removedWork = new Map<string, readonly string[]>();
  for (const action of input.actions) {
    if (action.actionType === 'remove_position') {
      const existing = afterById.get(action.positionId);
      if (!existing || action.fullyLoadedAnnualCostUsd !== null) {
        return blocked(
          'invalid_formula_input',
          'Delayering remove actions must reference an existing position and cannot supply cost.'
        );
      }
      removedWork.set(action.positionId, existing.assignedWorkPackageIds);
      afterById.delete(action.positionId);
      removedPositionIds.push(action.positionId);
      continue;
    }
    if (action.actionType === 'add_position') {
      if (
        afterById.has(action.positionId) ||
        !isFiniteNonNegative(action.fullyLoadedAnnualCostUsd)
      ) {
        return blocked(
          'invalid_formula_input',
          'Delayering add actions require a new position and governed non-negative cost.'
        );
      }
      afterById.set(action.positionId, {
        positionId: action.positionId,
        managerPositionId: action.targetManagerPositionId,
        fullyLoadedAnnualCostUsd: action.fullyLoadedAnnualCostUsd,
        assignedWorkPackageIds: [...action.assignedWorkPackageIds],
      });
      addedPositionIds.push(action.positionId);
      continue;
    }
    const existing = afterById.get(action.positionId);
    if (!existing || action.fullyLoadedAnnualCostUsd !== null) {
      return blocked(
        'invalid_formula_input',
        'Delayering manager-change actions must reference an existing position and cannot change cost.'
      );
    }
    afterById.set(action.positionId, {
      ...existing,
      managerPositionId: action.targetManagerPositionId,
    });
    reparentedPositionIds.push(action.positionId);
  }
  for (const action of input.actions.filter(
    (candidate) => candidate.actionType === 'remove_position'
  )) {
    const workPackageIds = removedWork.get(action.positionId) ?? [];
    if (workPackageIds.length === 0) continue;
    const target = action.reassignWorkToPositionId
      ? afterById.get(action.reassignWorkToPositionId)
      : null;
    if (!target) {
      return blocked('invalid_formula_input', 'Delayering actions cannot orphan required work.');
    }
    afterById.set(target.positionId, {
      ...target,
      assignedWorkPackageIds: [...new Set([...target.assignedWorkPackageIds, ...workPackageIds])],
    });
  }
  const afterPositions = [...afterById.values()];
  const afterMetrics = graphMetrics(afterPositions);
  const beforeWork = new Set(
    input.baselinePositions.flatMap((position) => position.assignedWorkPackageIds)
  );
  const afterWork = new Set(afterPositions.flatMap((position) => position.assignedWorkPackageIds));
  if (
    !afterMetrics ||
    beforeWork.size !== afterWork.size ||
    [...beforeWork].some((workPackageId) => !afterWork.has(workPackageId))
  ) {
    return blocked(
      'invalid_formula_input',
      'Delayering target graph is cyclic, has invalid roots, or loses required work.'
    );
  }
  const baselineFullyLoadedOrgCostUsd = roundUsd(
    input.baselinePositions.reduce((sum, position) => sum + position.fullyLoadedAnnualCostUsd, 0)
  );
  const recurringCostRemovedUsd = roundUsd(
    removedPositionIds.reduce(
      (sum, positionId) => sum + (beforeById.get(positionId)?.fullyLoadedAnnualCostUsd ?? 0),
      0
    )
  );
  const recurringCostAddedUsd = roundUsd(
    addedPositionIds.reduce(
      (sum, positionId) => sum + (afterById.get(positionId)?.fullyLoadedAnnualCostUsd ?? 0),
      0
    )
  );
  const netRunRateUsd = roundUsd(
    recurringCostRemovedUsd - recurringCostAddedUsd - input.recurringLeakageUsd
  );
  const inPeriodNetCashUsd = roundUsd(
    input.monthlyNetCashUsd.reduce((sum, value) => sum + value, 0)
  );
  const npvUsd = roundUsd(
    input.monthlyNetCashUsd.reduce(
      (sum, value, index) => sum + value / Math.pow(1 + input.discountRateAnnual, (index + 1) / 12),
      0
    )
  );
  const oneTimeCashUsd = roundUsd(
    input.oneTimeCashUsdByMonth.reduce((sum, value) => sum + value, 0)
  );
  const capacityGapFte = roundMetric(
    input.capacityRows.reduce((sum, row) => sum + Math.max(0, row.requiredFte - row.assignedFte), 0)
  );
  const serviceDeltaPct = roundMetric(
    Math.min(
      ...input.serviceOutcomeRows.map((row) =>
        row.direction === 'higher_is_better'
          ? row.scenarioValue - row.baselineValue
          : row.baselineValue - row.scenarioValue
      )
    )
  );
  return ok({
    primaryMetricKey: 'org_delayering_net_run_rate_usd',
    primaryMetricValue: netRunRateUsd,
    baselinePositionCount: input.baselinePositions.length,
    baselineFullyLoadedOrgCostUsd,
    substratePositionCount: input.substrateAnchor.positionCount,
    substrateFullyLoadedOrgCostUsd: input.substrateAnchor.fullyLoadedAnnualCostUsd,
    afterPositionCount: afterPositions.length,
    afterFullyLoadedOrgCostUsd: roundUsd(
      afterPositions.reduce((sum, position) => sum + position.fullyLoadedAnnualCostUsd, 0)
    ),
    netRunRateUsd,
    oneTimeCashUsd,
    inPeriodNetCashUsd,
    npvUsd,
    capacityGapFte,
    serviceDeltaPct,
    ebitdaLabel: 'finance_reconciled_net_ebitda',
    financeDefinitionRef: input.financeDefinitionRef,
    mappedGlAccountIds: Object.freeze([...input.mappedGlAccountIds].sort()),
    graphDelta: Object.freeze({
      beforePositionCount: input.baselinePositions.length,
      afterPositionCount: afterPositions.length,
      beforeManagerCount: beforeMetrics.managerCount,
      afterManagerCount: afterMetrics.managerCount,
      beforeMaxDepth: beforeMetrics.maxDepth,
      afterMaxDepth: afterMetrics.maxDepth,
      removedPositionIds: Object.freeze(removedPositionIds.sort()),
      addedPositionIds: Object.freeze(addedPositionIds.sort()),
      reparentedPositionIds: Object.freeze(reparentedPositionIds.sort()),
    }),
  });
}

function confidenceRank(value: OrgCostStructureOptionVector['confidenceRung']): number {
  return Number(value.slice(1));
}

function optionDominates(
  left: OrgCostStructureOptionVector,
  right: OrgCostStructureOptionVector
): boolean {
  const noWorse =
    left.netRunRateUsd >= right.netRunRateUsd &&
    left.oneTimeCashUsd <= right.oneTimeCashUsd &&
    left.npvUsd >= right.npvUsd &&
    left.paybackMonth <= right.paybackMonth &&
    left.firstBenefitMonth <= right.firstBenefitMonth &&
    left.fullRunRateMonth <= right.fullRunRateMonth &&
    left.scheduleP50Month <= right.scheduleP50Month &&
    left.scheduleP80Month <= right.scheduleP80Month &&
    left.capacityGapFte <= right.capacityGapFte &&
    left.serviceDeltaPct >= right.serviceDeltaPct &&
    confidenceRank(left.confidenceRung) >= confidenceRank(right.confidenceRung);
  const better =
    left.netRunRateUsd > right.netRunRateUsd ||
    left.oneTimeCashUsd < right.oneTimeCashUsd ||
    left.npvUsd > right.npvUsd ||
    left.paybackMonth < right.paybackMonth ||
    left.firstBenefitMonth < right.firstBenefitMonth ||
    left.fullRunRateMonth < right.fullRunRateMonth ||
    left.scheduleP50Month < right.scheduleP50Month ||
    left.scheduleP80Month < right.scheduleP80Month ||
    left.capacityGapFte < right.capacityGapFte ||
    left.serviceDeltaPct > right.serviceDeltaPct ||
    confidenceRank(left.confidenceRung) > confidenceRank(right.confidenceRung);
  return noWorse && better;
}

function executeOrgCompareCostStructureOptions(
  input: OrgCompareCostStructureOptionsInput
): ApprovedFormulaResult<OrgCompareCostStructureOptionsOutput> {
  if (
    !input ||
    !hasValidSubstrateAnchor(input) ||
    !Array.isArray(input.options) ||
    input.options.length < 2
  ) {
    return blocked(
      'missing_formula_input',
      'Cost-structure comparison requires at least two governed options.'
    );
  }
  const ids = new Set(input.options.map((option) => option.optionId));
  if (
    ids.size !== input.options.length ||
    !input.options.some((option) => option.optionType === 'do_nothing') ||
    input.options.some((option) =>
      [
        option.netRunRateUsd,
        option.oneTimeCashUsd,
        option.npvUsd,
        option.paybackMonth,
        option.firstBenefitMonth,
        option.fullRunRateMonth,
        option.scheduleP50Month,
        option.scheduleP80Month,
        option.capacityGapFte,
        option.serviceDeltaPct,
      ].some((value) => !Number.isFinite(value))
    )
  ) {
    return blocked(
      'invalid_formula_input',
      'Cost-structure options require unique ids, a do-nothing comparator, and finite vector values.'
    );
  }
  const eligible = input.options.filter((option) => option.legalGate === 'pass');
  const pareto = eligible
    .filter(
      (candidate) =>
        !eligible.some(
          (other) => other.optionId !== candidate.optionId && optionDominates(other, candidate)
        )
    )
    .map((option) => option.optionId)
    .sort();
  return ok({
    primaryMetricKey: 'pareto_option_count',
    primaryMetricValue: pareto.length,
    optionCount: input.options.length,
    paretoOptionIds: Object.freeze(pareto),
    blockedOptionIds: Object.freeze(
      input.options
        .filter((option) => option.legalGate === 'block')
        .map((option) => option.optionId)
        .sort()
    ),
    options: Object.freeze([...input.options]),
    additiveValueUsd: null,
  });
}

const WORK_REDIESIGN_PREDICATES = Object.freeze([
  ['workBound', 'work_disposition_missing'],
  ['resourceOrVendorActionBound', 'resource_or_vendor_action_missing'],
  ['financeAccountBound', 'finance_account_missing'],
  ['ownerBound', 'owner_missing'],
  ['milestoneBound', 'milestone_missing'],
  ['overlapKeysBound', 'overlap_keys_missing'],
] as const);

function executeOrgSavingsWorkRedesignGate(
  input: OrgSavingsWorkRedesignGateInput
): ApprovedFormulaResult<OrgSavingsWorkRedesignGateOutput> {
  if (
    !input ||
    !hasValidSubstrateAnchor(input) ||
    !Array.isArray(input.contributions) ||
    input.contributions.length === 0
  ) {
    return blocked('missing_formula_input', 'Work-redesign gate requires contribution leaves.');
  }
  if (input.contributions.some((item) => item.valueDomain === 'workforce')) {
    return blocked(
      'never_blend_violation',
      'Org-cost work-redesign value must never sum workforce-owned dollars.'
    );
  }
  const ids = new Set(input.contributions.map((item) => item.contributionId));
  if (
    ids.size !== input.contributions.length ||
    input.contributions.some((item) => !isFiniteNonNegative(item.netRunRateUsd))
  ) {
    return blocked(
      'invalid_formula_input',
      'Work-redesign contributions require unique ids and finite non-negative org-cost value.'
    );
  }
  const withheld: Record<string, readonly string[]> = {};
  const eligibleContributionIds: string[] = [];
  let bankable = 0;
  let withheldValue = 0;
  for (const contribution of input.contributions) {
    const failed = WORK_REDIESIGN_PREDICATES.filter(([field]) => !contribution[field]).map(
      ([, reason]) => reason
    );
    if (failed.length === 0) {
      eligibleContributionIds.push(contribution.contributionId);
      bankable += contribution.netRunRateUsd;
    } else {
      withheld[contribution.contributionId] = Object.freeze(failed);
      withheldValue += contribution.netRunRateUsd;
    }
  }
  return ok({
    primaryMetricKey: 'bankable_org_net_run_rate_usd',
    primaryMetricValue: roundUsd(bankable),
    bankableNetRunRateUsd: roundUsd(bankable),
    withheldNetRunRateUsd: roundUsd(withheldValue),
    eligibleContributionIds: Object.freeze(eligibleContributionIds.sort()),
    withheldPredicatesByContributionId: Object.freeze(withheld),
  });
}

function executeOrgPeopleRiskAdjustedNetEbitda(
  input: OrgPeopleRiskAdjustedNetEbitdaInput
): ApprovedFormulaResult<OrgPeopleRiskAdjustedNetEbitdaOutput> {
  if (
    !input ||
    !hasValidSubstrateAnchor(input) ||
    !isFiniteNonNegative(input.financeReconciledOrgNetRunRateUsd) ||
    !isFiniteNonNegative(input.capacityOrServiceLeakageUsd) ||
    !isNonEmptyString(input.calibrationDatasetId) ||
    !Array.isArray(input.attritionCohorts) ||
    input.attritionCohorts.length === 0
  ) {
    return blocked(
      'missing_formula_input',
      'People-risk adjustment requires finance-reconciled org value and calibrated cohort evidence.'
    );
  }
  const costs: Record<string, number> = {};
  const distributions: Record<string, readonly number[]> = {};
  let drawCount: number | null = null;
  for (const cohort of input.attritionCohorts) {
    if (
      !isNonEmptyString(cohort.cohortId) ||
      !Number.isInteger(cohort.eligibleCount) ||
      cohort.eligibleCount <= 0 ||
      !Array.isArray(cohort.observedExitRateSamples) ||
      cohort.observedExitRateSamples.length < 3 ||
      cohort.observedExitRateSamples.some(
        (rate: number) => !isFiniteNonNegative(rate) || rate > 1
      ) ||
      !isFiniteNonNegative(cohort.baselineExitRate) ||
      cohort.baselineExitRate > 1 ||
      !isFiniteNonNegative(cohort.replacementAndRampCostUsd)
    ) {
      return blocked(
        'invalid_formula_input',
        'People-risk cohorts require aggregate counts, bounded rates, and observed replacement/ramp cost.'
      );
    }
    if (drawCount !== null && drawCount !== cohort.observedExitRateSamples.length) {
      return blocked(
        'invalid_formula_input',
        'People-risk cohort distributions must share one calibrated draw count.'
      );
    }
    drawCount = cohort.observedExitRateSamples.length;
    const distribution = cohort.observedExitRateSamples.map((observedExitRate: number) =>
      roundUsd(
        Math.max(0, observedExitRate - cohort.baselineExitRate) *
          cohort.eligibleCount *
          cohort.replacementAndRampCostUsd
      )
    );
    distributions[cohort.cohortId] = Object.freeze(distribution);
    costs[cohort.cohortId] = roundUsd(
      distribution.reduce((sum: number, value: number) => sum + value, 0) / distribution.length
    );
  }
  const aggregateDistribution = Array.from({ length: drawCount ?? 0 }, (_, index) =>
    roundUsd(
      Object.values(distributions).reduce((sum, distribution) => sum + distribution[index], 0)
    )
  );
  const expectedIncrementalAttritionCostUsd = roundUsd(
    Object.values(costs).reduce((sum, value) => sum + value, 0)
  );
  const riskAdjustedNetRunRateUsd = roundUsd(
    input.financeReconciledOrgNetRunRateUsd -
      expectedIncrementalAttritionCostUsd -
      input.capacityOrServiceLeakageUsd
  );
  return ok({
    primaryMetricKey: 'people_risk_adjusted_org_net_run_rate_usd',
    primaryMetricValue: riskAdjustedNetRunRateUsd,
    financeReconciledOrgNetRunRateUsd: roundUsd(input.financeReconciledOrgNetRunRateUsd),
    expectedIncrementalAttritionCostUsd,
    capacityOrServiceLeakageUsd: roundUsd(input.capacityOrServiceLeakageUsd),
    riskAdjustedNetRunRateUsd,
    cohortIncrementalExitCostUsd: Object.freeze(costs),
    cohortIncrementalExitCostDistributionUsd: Object.freeze(distributions),
    aggregateIncrementalExitCostDistributionUsd: Object.freeze(aggregateDistribution),
  });
}

function executeOrgOptionsDecisionDashboard(
  input: OrgOptionsDecisionDashboardInput
): ApprovedFormulaResult<OrgOptionsDecisionDashboardOutput> {
  if (
    !input ||
    !hasValidSubstrateAnchor(input) ||
    !Array.isArray(input.cells) ||
    input.cells.length === 0
  ) {
    return blocked('missing_formula_input', 'Options dashboard requires typed governed cells.');
  }
  const identity = input.identity;
  const metricKeys = new Set<string>();
  for (const cell of input.cells) {
    if (
      !isNonEmptyString(cell.metricKey) ||
      metricKeys.has(cell.metricKey) ||
      !Number.isFinite(cell.metricValue) ||
      !isNonEmptyString(cell.formulaRef) ||
      !isNonEmptyString(cell.evidenceRefId) ||
      cell.optionId !== identity.optionId ||
      cell.optionVersion !== identity.optionVersion ||
      cell.snapshotId !== identity.snapshotId ||
      cell.scopeId !== identity.scopeId ||
      cell.currency !== identity.currency ||
      cell.horizonStart !== identity.horizonStart ||
      cell.horizonEnd !== identity.horizonEnd
    ) {
      return blocked(
        'invalid_formula_input',
        'Options dashboard cells must have unique metrics and exact option/snapshot/scope/currency/horizon identity.'
      );
    }
    metricKeys.add(cell.metricKey);
  }
  return ok({
    primaryMetricKey: 'identity_consistent_dashboard_metric_count',
    primaryMetricValue: metricKeys.size,
    projectedMetricCount: metricKeys.size,
    metricKeys: Object.freeze([...metricKeys].sort()),
    identity: Object.freeze({ ...identity }),
    cells: Object.freeze(
      [...input.cells]
        .sort((left, right) => left.metricKey.localeCompare(right.metricKey))
        .map((cell) => Object.freeze({ ...cell }))
    ),
    additiveValueUsd: null,
  });
}

function recommendationOptionDominates(
  left: OrgRecommendationOption,
  right: OrgRecommendationOption,
  axes: readonly string[]
): boolean {
  const noWorse = axes.every((axis) => left.axisScores[axis] >= right.axisScores[axis]);
  const better = axes.some((axis) => left.axisScores[axis] > right.axisScores[axis]);
  return noWorse && better;
}

function executeOrgRecommendedOptionRationale(
  input: OrgRecommendedOptionRationaleInput
): ApprovedFormulaResult<OrgRecommendedOptionRationaleOutput> {
  if (
    !input ||
    !hasValidSubstrateAnchor(input) ||
    !isNonEmptyString(input.executiveRationale) ||
    !Array.isArray(input.tradeoffs) ||
    input.tradeoffs.length === 0 ||
    !Array.isArray(input.decisiveEvidenceRefIds) ||
    input.decisiveEvidenceRefIds.length === 0 ||
    !Array.isArray(input.assumptions) ||
    !Array.isArray(input.dataGaps) ||
    !isNonEmptyString(input.consequenceOfDelay) ||
    !isNonEmptyString(input.ownerRoleId) ||
    !isNonEmptyString(input.controlPlan) ||
    !Array.isArray(input.options) ||
    input.options.length < 2
  ) {
    return blocked(
      'missing_formula_input',
      'Recommendation requires governed options, rationale bindings, tradeoffs, evidence, ownership, and a control plan.'
    );
  }
  if (input.options.some((option) => String(option.optionType) === 'workforce_reduction')) {
    return blocked(
      'never_blend_violation',
      'Org recommendation cannot select or score workforce-reduction options.'
    );
  }
  const optionIds = new Set(input.options.map((option) => option.optionId));
  const eligibleOptions = input.options.filter((option) => option.hardGatesPass);
  const axes = Object.keys(eligibleOptions[0]?.axisScores ?? {}).sort();
  if (
    optionIds.size !== input.options.length ||
    !input.options.some((option) => option.optionType === 'do_nothing') ||
    eligibleOptions.length === 0 ||
    axes.length < 2 ||
    input.options.some(
      (option) =>
        !isNonEmptyString(option.optionId) ||
        Object.keys(option.axisScores).sort().join('|') !== axes.join('|') ||
        axes.some(
          (axis) => !isFiniteNonNegative(option.axisScores[axis]) || option.axisScores[axis] > 1
        )
    )
  ) {
    return blocked(
      'invalid_formula_input',
      'Recommendation options require unique ids, do-nothing, common bounded axes, and at least one hard-gate survivor.'
    );
  }
  const paretoOptions = eligibleOptions.filter(
    (candidate) =>
      !eligibleOptions.some(
        (other) =>
          other.optionId !== candidate.optionId &&
          recommendationOptionDominates(other, candidate, axes)
      )
  );
  let selected: Readonly<{ optionId: string; score: number }> | null = null;
  if (input.decisionPolicy !== null) {
    const weightEntries = Object.entries(input.decisionPolicy.weights);
    if (
      !isNonEmptyString(input.decisionPolicy.policyId) ||
      !Number.isInteger(input.decisionPolicy.policyVersion) ||
      input.decisionPolicy.policyVersion <= 0 ||
      weightEntries.length !== axes.length ||
      weightEntries.some(
        ([axis, weight]) => !axes.includes(axis) || !isFiniteNonNegative(weight)
      ) ||
      Math.abs(weightEntries.reduce((sum, [, weight]) => sum + weight, 0) - 1) > EPSILON
    ) {
      return blocked(
        'invalid_formula_input',
        'Recommendation policy weights must cover the approved axes, be non-negative, and sum exactly to one.'
      );
    }
    const scored = paretoOptions
      .map((option) => ({
        optionId: option.optionId,
        score: roundMetric(
          weightEntries.reduce((sum, [axis, weight]) => sum + option.axisScores[axis] * weight, 0)
        ),
      }))
      .sort(
        (left, right) => right.score - left.score || left.optionId.localeCompare(right.optionId)
      );
    selected = scored[0] ?? null;
  }
  const paretoOptionIds = paretoOptions.map((option) => option.optionId).sort();
  return ok({
    primaryMetricKey: 'recommended_option_policy_score',
    primaryMetricValue: selected?.score ?? 0,
    recommendedOptionId: selected?.optionId ?? null,
    recommendedOptionScore: selected?.score ?? null,
    eligibleOptionCount: eligibleOptions.length,
    paretoOptionIds: Object.freeze(paretoOptionIds),
    rejectedOptionIds: Object.freeze(
      input.options
        .filter((option) =>
          selected !== null ? option.optionId !== selected.optionId : !option.hardGatesPass
        )
        .map((option) => option.optionId)
        .sort()
    ),
    policyId: input.decisionPolicy?.policyId ?? null,
    policyVersion: input.decisionPolicy?.policyVersion ?? null,
    executiveRationale: input.executiveRationale,
    tradeoffs: Object.freeze([...input.tradeoffs]),
    decisiveEvidenceRefIds: Object.freeze([...input.decisiveEvidenceRefIds]),
    assumptions: Object.freeze([...input.assumptions]),
    dataGaps: Object.freeze([...input.dataGaps]),
    consequenceOfDelay: input.consequenceOfDelay,
    ownerRoleId: input.ownerRoleId,
    controlPlan: input.controlPlan,
  });
}

function percentile(values: readonly number[], percentileValue: number): number {
  const sorted = [...values].sort((left, right) => left - right);
  const index = Math.max(
    0,
    Math.min(sorted.length - 1, Math.ceil(percentileValue * sorted.length) - 1)
  );
  return sorted[index];
}

function integratedScheduleCompletionDay(
  workPackages: OrgExecutionRiskTimeToRealizeInput['workPackages'],
  durationMultiplierByWorkPackageId: Readonly<Record<string, number>>
): number | null {
  const byId = new Map(workPackages.map((workPackage) => [workPackage.workPackageId, workPackage]));
  if (
    byId.size !== workPackages.length ||
    Object.keys(durationMultiplierByWorkPackageId).sort().join('|') !==
      [...byId.keys()].sort().join('|')
  ) {
    return null;
  }
  const finish = new Map<string, number>();
  while (finish.size < workPackages.length) {
    let progressed = false;
    for (const workPackage of workPackages) {
      if (finish.has(workPackage.workPackageId)) continue;
      if (
        !isNonEmptyString(workPackage.workPackageId) ||
        !Number.isInteger(workPackage.durationDays) ||
        workPackage.durationDays <= 0 ||
        workPackage.predecessorIds.some(
          (predecessorId) =>
            !byId.has(predecessorId) ||
            predecessorId === workPackage.workPackageId ||
            !finish.has(predecessorId)
        )
      ) {
        continue;
      }
      const multiplier = durationMultiplierByWorkPackageId[workPackage.workPackageId];
      if (!Number.isFinite(multiplier) || multiplier <= 0) return null;
      const predecessorFinish = Math.max(
        0,
        ...workPackage.predecessorIds.map((predecessorId) => finish.get(predecessorId) ?? 0)
      );
      finish.set(
        workPackage.workPackageId,
        predecessorFinish + Math.ceil(workPackage.durationDays * multiplier)
      );
      progressed = true;
    }
    if (!progressed) return null;
  }
  return Math.max(...finish.values());
}

function executeOrgExecutionRiskTimeToRealize(
  input: OrgExecutionRiskTimeToRealizeInput
): ApprovedFormulaResult<OrgExecutionRiskTimeToRealizeOutput> {
  if (
    !input ||
    !hasValidSubstrateAnchor(input) ||
    (input.mode !== 'calibrated_distribution' && input.mode !== 'deterministic_sensitivity') ||
    !Array.isArray(input.workPackages) ||
    input.workPackages.length === 0 ||
    !isFiniteNonNegative(input.baseEligibleValueUsd) ||
    !Array.isArray(input.draws) ||
    input.draws.length < 3 ||
    !Number.isInteger(input.simulationSeed) ||
    !Number.isInteger(input.iterationCount) ||
    input.iterationCount !== input.draws.length ||
    !isNonEmptyString(input.algorithmVersion) ||
    !isNonEmptyString(input.correlationModelVersion) ||
    !hasValidDistributionCorrelationReceipt(input)
  ) {
    return blocked(
      'invalid_formula_input',
      'Execution-risk timing requires an integrated schedule, approved risk draws, and reproducible simulation metadata.'
    );
  }
  if (input.mode === 'calibrated_distribution' && !isNonEmptyString(input.calibrationDatasetId)) {
    return blocked(
      'missing_formula_input',
      'Calibrated execution percentiles require a calibration dataset.'
    );
  }
  const drawIds = new Set(input.draws.map((draw) => draw.drawId));
  const completionDayByDrawId: Record<string, number> = {};
  const valueUsdByDrawId: Record<string, number> = {};
  if (drawIds.size !== input.draws.length) {
    return blocked('invalid_formula_input', 'Execution-risk draws require unique identities.');
  }
  for (const draw of input.draws) {
    if (
      !isNonEmptyString(draw.drawId) ||
      !isNonEmptyString(draw.driverId) ||
      !isFiniteNonNegative(draw.valueErosionUsd)
    ) {
      return blocked(
        'invalid_formula_input',
        'Execution-risk draws require named non-negative risk effects.'
      );
    }
    const completionDay = integratedScheduleCompletionDay(
      input.workPackages,
      draw.durationMultiplierByWorkPackageId
    );
    if (completionDay === null) {
      return blocked(
        'invalid_formula_input',
        'Execution-risk schedule is cyclic, incomplete, or missing a duration multiplier.'
      );
    }
    completionDayByDrawId[draw.drawId] = completionDay;
    valueUsdByDrawId[draw.drawId] = roundUsd(input.baseEligibleValueUsd - draw.valueErosionUsd);
  }
  const completionDaySamples = Object.values(completionDayByDrawId);
  const completionDayWeightedSamples = input.draws.map((draw) => ({
    value: completionDayByDrawId[draw.drawId],
    weight: draw.jointProbabilityWeight,
  }));
  const valueUsdWeightedSamples = input.draws.map((draw) => ({
    value: valueUsdByDrawId[draw.drawId],
    weight: draw.jointProbabilityWeight,
  }));
  const baseDraw = input.draws.find((draw) => draw.driverId === 'base');
  if (!baseDraw) {
    return blocked(
      'missing_formula_input',
      'Execution-risk sensitivities require one approved base draw.'
    );
  }
  const baseCompletionDay = completionDayByDrawId[baseDraw.drawId];
  const baseValueUsd = valueUsdByDrawId[baseDraw.drawId];
  const driverSensitivityById: Record<
    string,
    Readonly<{ completionDayDelta: number; valueDownsideUsd: number }>
  > = {};
  for (const draw of input.draws) {
    if (draw.driverId === 'base') continue;
    const candidate = Object.freeze({
      completionDayDelta: completionDayByDrawId[draw.drawId] - baseCompletionDay,
      valueDownsideUsd: roundUsd(Math.max(0, baseValueUsd - valueUsdByDrawId[draw.drawId])),
    });
    const current = driverSensitivityById[draw.driverId];
    if (
      !current ||
      Math.abs(candidate.completionDayDelta) > Math.abs(current.completionDayDelta) ||
      candidate.valueDownsideUsd > current.valueDownsideUsd
    ) {
      driverSensitivityById[draw.driverId] = candidate;
    }
  }
  const calibrated = input.mode === 'calibrated_distribution';
  const completionP50Day = calibrated
    ? weightedPercentile(completionDayWeightedSamples, 0.5)
    : null;
  const completionP80Day = calibrated
    ? weightedPercentile(completionDayWeightedSamples, 0.8)
    : null;
  const sensitivityDownsideCompletionDay = calibrated ? null : Math.max(...completionDaySamples);
  const primary = completionP80Day ?? sensitivityDownsideCompletionDay ?? 0;
  return ok({
    primaryMetricKey: 'execution_risk_completion_day',
    primaryMetricValue: primary,
    posture: input.mode,
    completionP50Day,
    completionP80Day,
    sensitivityDownsideCompletionDay,
    valueP20Usd: calibrated ? roundUsd(weightedPercentile(valueUsdWeightedSamples, 0.2)) : null,
    completionDayByDrawId: Object.freeze(completionDayByDrawId),
    valueUsdByDrawId: Object.freeze(valueUsdByDrawId),
    driverSensitivityById: Object.freeze(driverSensitivityById),
    simulationSeed: input.simulationSeed,
    iterationCount: input.iterationCount,
    algorithmVersion: input.algorithmVersion,
    correlationModelVersion: input.correlationModelVersion,
    distributionCorrelationReceipt: input.distributionCorrelationReceipt,
  });
}

function executeOrgRealizationConfidenceBand(
  input: OrgRealizationConfidenceBandInput
): ApprovedFormulaResult<OrgRealizationConfidenceBandOutput> {
  if (
    !input ||
    !hasValidSubstrateAnchor(input) ||
    (input.mode !== 'calibrated_confidence' && input.mode !== 'scenario_range') ||
    !Number.isInteger(input.simulationSeed) ||
    !Number.isInteger(input.iterationCount) ||
    !Array.isArray(input.draws) ||
    input.draws.length < 3 ||
    input.iterationCount !== input.draws.length ||
    !isNonEmptyString(input.algorithmVersion) ||
    !isNonEmptyString(input.correlationModelVersion) ||
    !hasValidDistributionCorrelationReceipt(input) ||
    !Array.isArray(input.observedActualsUsdByMonth)
  ) {
    return blocked(
      'invalid_formula_input',
      'Realization range requires reproducible simulation metadata.'
    );
  }
  const drawIds = new Set(input.draws.map((draw) => draw.drawId));
  const realizedValueByDrawId: Record<string, number> = {};
  const monthlyValueByDrawId: Record<string, readonly number[]> = {};
  let monthCount: number | null = null;
  if (drawIds.size !== input.draws.length) {
    return blocked('invalid_formula_input', 'Realization draws require unique identities.');
  }
  for (const draw of input.draws) {
    if (
      !isNonEmptyString(draw.drawId) ||
      !isFiniteNonNegative(draw.eligibleRunRateUsd) ||
      !Array.isArray(draw.rampFractions) ||
      draw.rampFractions.length === 0 ||
      draw.rampFractions.some(
        (fraction: number) => !isFiniteNonNegative(fraction) || fraction > 1
      ) ||
      !Array.isArray(draw.recurringLeakageUsdByMonth) ||
      !Array.isArray(draw.oneTimeCashUsdByMonth) ||
      draw.recurringLeakageUsdByMonth.length !== draw.rampFractions.length ||
      draw.oneTimeCashUsdByMonth.length !== draw.rampFractions.length ||
      draw.recurringLeakageUsdByMonth.some((value: number) => !isFiniteNonNegative(value)) ||
      draw.oneTimeCashUsdByMonth.some((value: number) => !isFiniteNonNegative(value)) ||
      (monthCount !== null && monthCount !== draw.rampFractions.length)
    ) {
      return blocked(
        'invalid_formula_input',
        'Realization draws require aligned monthly ramp, leakage, and one-time-cash inputs.'
      );
    }
    monthCount = draw.rampFractions.length;
    const monthlyValues = draw.rampFractions.map((rampFraction: number, index: number) =>
      roundUsd(
        draw.eligibleRunRateUsd * rampFraction -
          draw.recurringLeakageUsdByMonth[index] -
          draw.oneTimeCashUsdByMonth[index]
      )
    );
    monthlyValueByDrawId[draw.drawId] = Object.freeze(monthlyValues);
    realizedValueByDrawId[draw.drawId] = roundUsd(
      monthlyValues.reduce((sum: number, value: number) => sum + value, 0)
    );
  }
  const realizedValueSamplesUsd = Object.values(realizedValueByDrawId);
  const realizedValueWeightedSamplesUsd = input.draws.map((draw) => ({
    value: realizedValueByDrawId[draw.drawId],
    weight: draw.jointProbabilityWeight,
  }));
  const reproducibilityReceipt = {
    simulationSeed: input.simulationSeed,
    iterationCount: input.iterationCount,
    algorithmVersion: input.algorithmVersion,
    correlationModelVersion: input.correlationModelVersion,
    inputDrawIds: Object.freeze([...drawIds].sort()),
    distributionCorrelationReceipt: input.distributionCorrelationReceipt,
  };
  if (input.mode === 'calibrated_confidence') {
    if (
      !isNonEmptyString(input.calibrationDatasetId) ||
      input.observedActualsUsdByMonth.length !== monthCount ||
      input.observedActualsUsdByMonth.some((value) => !Number.isFinite(value))
    ) {
      return blocked(
        'missing_formula_input',
        'Confidence labels require observed monthly actuals and a calibration dataset.'
      );
    }
    const p10Usd = roundUsd(weightedPercentile(realizedValueWeightedSamplesUsd, 0.1));
    const p50Usd = roundUsd(weightedPercentile(realizedValueWeightedSamplesUsd, 0.5));
    const p90Usd = roundUsd(weightedPercentile(realizedValueWeightedSamplesUsd, 0.9));
    const rangeWidthUsd = roundUsd(p90Usd - p10Usd);
    const baseDrawId = drawIds.has('base')
      ? 'base'
      : [...drawIds].sort()[Math.floor(drawIds.size / 2)];
    const baseMonthlyValues = monthlyValueByDrawId[baseDrawId];
    const baseMeanAbsoluteErrorUsd = roundUsd(
      baseMonthlyValues.reduce(
        (sum, value, index) => sum + Math.abs(value - input.observedActualsUsdByMonth[index]),
        0
      ) / baseMonthlyValues.length
    );
    return ok({
      primaryMetricKey: 'realization_value_range_width_usd',
      primaryMetricValue: rangeWidthUsd,
      posture: 'calibrated_confidence',
      p10Usd,
      p50Usd,
      p90Usd,
      scenarioLowUsd: null,
      scenarioBaseUsd: null,
      scenarioHighUsd: null,
      rangeWidthUsd,
      calibrationDatasetId: input.calibrationDatasetId,
      realizedValueByDrawId: Object.freeze(realizedValueByDrawId),
      calibration: Object.freeze({
        observedMonthCount: input.observedActualsUsdByMonth.length,
        baseMeanAbsoluteErrorUsd,
      }),
      ...reproducibilityReceipt,
    });
  }
  const orderedValues = [...realizedValueSamplesUsd].sort((left, right) => left - right);
  const scenarioLowUsd = orderedValues[0];
  const scenarioHighUsd = orderedValues[orderedValues.length - 1];
  const scenarioBaseUsd = drawIds.has('base')
    ? realizedValueByDrawId.base
    : percentile(orderedValues, 0.5);
  const rangeWidthUsd = roundUsd(scenarioHighUsd - scenarioLowUsd);
  return ok({
    primaryMetricKey: 'realization_value_range_width_usd',
    primaryMetricValue: rangeWidthUsd,
    posture: 'scenario_range',
    p10Usd: null,
    p50Usd: null,
    p90Usd: null,
    scenarioLowUsd,
    scenarioBaseUsd,
    scenarioHighUsd,
    rangeWidthUsd,
    calibrationDatasetId: null,
    realizedValueByDrawId: Object.freeze(realizedValueByDrawId),
    calibration: null,
    ...reproducibilityReceipt,
  });
}

const ORG_ANALYSIS_FIXTURE_FORMULA_EVIDENCE_REF_IDS = Object.freeze({
  org_time_to_realize_v1: [
    ORG_ANALYSIS_FIXTURE_SUBSTRATE_ANCHOR.evidenceRefId,
    'option_schedule',
    'option_action_plan',
  ],
  org_delayering_net_ebitda_scenario_v1: [
    ORG_ANALYSIS_FIXTURE_SUBSTRATE_ANCHOR.evidenceRefId,
    'org_state_as_is',
    'option_action_plan',
    'position_cost_roster',
    'activity_work_assignment',
    'option_cost_cash_flow',
    'finance_anchor',
    'operating_outcome_fact',
  ],
  org_compare_cost_structure_options_v1: [
    ORG_ANALYSIS_FIXTURE_SUBSTRATE_ANCHOR.evidenceRefId,
    'option_definition',
    'option_action_plan',
    'option_schedule',
    'option_cost_cash_flow',
    'initiative_capacity_binding',
    'operating_outcome_fact',
    'workforce_legal_gate',
    'finance_anchor',
  ],
  org_savings_work_redesign_gate_v1: [
    ORG_ANALYSIS_FIXTURE_SUBSTRATE_ANCHOR.evidenceRefId,
    'org_cost_sub_lever_buildup',
    'activity_work_assignment',
    'option_action_plan',
    'option_schedule',
    'option_cost_cash_flow',
    'finance_anchor',
  ],
  org_people_risk_adjusted_net_ebitda_v1: [
    ORG_ANALYSIS_FIXTURE_SUBSTRATE_ANCHOR.evidenceRefId,
    'org_cost_sub_lever_buildup',
    'workforce_attrition_cohort',
    'initiative_capacity_binding',
    'operating_outcome_fact',
    'finance_anchor',
  ],
  org_options_decision_dashboard_v1: [
    ORG_ANALYSIS_FIXTURE_SUBSTRATE_ANCHOR.evidenceRefId,
    'option_definition',
    'option_action_plan',
    'option_schedule',
    'option_cost_cash_flow',
    'initiative_capacity_binding',
    'operating_outcome_fact',
    'workforce_legal_gate',
    'finance_anchor',
    'intent_evidence_manifest',
    'evidence-economics',
    'evidence-schedule',
    'evidence-service',
  ],
  org_recommended_option_rationale_v1: [
    ORG_ANALYSIS_FIXTURE_SUBSTRATE_ANCHOR.evidenceRefId,
    'option_definition',
    'option_decision_policy',
    'option_cost_cash_flow',
    'option_schedule',
    'initiative_capacity_binding',
    'operating_outcome_fact',
    'workforce_legal_gate',
    'intent_evidence_manifest',
    'evidence-option-vector',
  ],
  org_execution_risk_time_to_realize_v1: [
    ORG_ANALYSIS_FIXTURE_SUBSTRATE_ANCHOR.evidenceRefId,
    'option_schedule',
    'scenario_risk_model',
    'option_cost_cash_flow',
    'historical_execution_actuals',
  ],
  org_realization_confidence_band_v1: [
    ORG_ANALYSIS_FIXTURE_SUBSTRATE_ANCHOR.evidenceRefId,
    'scenario_risk_model',
    'option_schedule',
    'option_cost_cash_flow',
    'realization_actuals',
    'finance_anchor',
  ],
} satisfies Record<OrgAnalysisDistinctScenarioFormulaId, readonly string[]>);

const ORG_ANALYSIS_FIXTURE_EVIDENCE_BINDING_REFS = [
  ...new Set(Object.values(ORG_ANALYSIS_FIXTURE_FORMULA_EVIDENCE_REF_IDS).flat()),
];

export const ORG_ANALYSIS_ANTI_ALIAS_FIXTURE_RAW_SNAPSHOT = Object.freeze({
  substrateAnchor: ORG_ANALYSIS_FIXTURE_SUBSTRATE_ANCHOR,
  reportingCutAsOf: '2026-07-10',
  formulaEvidenceRefIds: ORG_ANALYSIS_FIXTURE_FORMULA_EVIDENCE_REF_IDS,
  evidenceBindings: ORG_ANALYSIS_FIXTURE_EVIDENCE_BINDING_REFS.map((evidenceRefId) => ({
    evidenceRefId,
    evidenceArtifactId: `fixture-artifact:${evidenceRefId}`,
    formulaRef:
      evidenceRefId === ORG_ANALYSIS_FIXTURE_SUBSTRATE_ANCHOR.evidenceRefId
        ? ORG_ANALYSIS_FIXTURE_SUBSTRATE_ANCHOR.formulaRef
        : evidenceRefId === 'evidence-economics'
          ? 'formula-economics'
          : evidenceRefId === 'evidence-schedule'
            ? 'formula-schedule'
            : evidenceRefId === 'evidence-service'
              ? 'formula-service'
              : evidenceRefId === 'evidence-option-vector'
                ? 'org_compare_cost_structure_options_v1@1.0.0'
                : `evidence-contract:${evidenceRefId}@1.0.0`,
    sourceSystemRef: ORG_COST_OPTIMIZATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
    ...(evidenceRefId === ORG_ANALYSIS_FIXTURE_SUBSTRATE_ANCHOR.evidenceRefId
      ? {
          positionCount: ORG_ANALYSIS_FIXTURE_SUBSTRATE_ANCHOR.positionCount,
          fullyLoadedAnnualCostUsd: ORG_ANALYSIS_FIXTURE_SUBSTRATE_ANCHOR.fullyLoadedAnnualCostUsd,
        }
      : {}),
  })),
  timeToRealize: {
    optionId: 'delayer',
    optionVersion: 1,
    workPackages: [
      {
        workPackageId: 'design',
        predecessorIds: [],
        durationDays: 10,
        ownerRoleId: 'coo',
        startsBenefit: true,
        requiredForFullRunRate: false,
        cumulativeRunRateFraction: 0.25,
      },
      {
        workPackageId: 'deploy',
        predecessorIds: ['design'],
        durationDays: 20,
        ownerRoleId: 'coo',
        startsBenefit: false,
        requiredForFullRunRate: true,
        cumulativeRunRateFraction: 1,
      },
    ],
  },
  delayering: {
    optionId: 'delayer',
    optionVersion: 1,
    optionType: 'delayering',
    valueDomain: 'org_cost',
    baselinePositions: [
      {
        positionId: 'root',
        managerPositionId: null,
        fullyLoadedAnnualCostUsd: 600,
        assignedWorkPackageIds: [],
      },
      {
        positionId: 'manager',
        managerPositionId: 'root',
        fullyLoadedAnnualCostUsd: 200,
        assignedWorkPackageIds: ['manage-operations'],
      },
      {
        positionId: 'analyst',
        managerPositionId: 'manager',
        fullyLoadedAnnualCostUsd: 200,
        assignedWorkPackageIds: ['analyze-operations'],
      },
    ],
    actions: [
      {
        actionId: 'remove-analyst',
        actionType: 'remove_position',
        positionId: 'analyst',
        targetManagerPositionId: null,
        fullyLoadedAnnualCostUsd: null,
        assignedWorkPackageIds: [],
        reassignWorkToPositionId: 'manager',
      },
      {
        actionId: 'add-coordinator',
        actionType: 'add_position',
        positionId: 'coordinator',
        targetManagerPositionId: 'manager',
        fullyLoadedAnnualCostUsd: 50,
        assignedWorkPackageIds: [],
        reassignWorkToPositionId: null,
      },
    ],
    recurringLeakageUsd: 25,
    oneTimeCashUsdByMonth: [100, 0],
    monthlyNetCashUsd: [50, 50],
    discountRateAnnual: 0.12,
    capacityRows: [
      {
        workPackageId: 'analyze-operations',
        requiredFte: 2,
        assignedFte: 1,
      },
    ],
    serviceOutcomeRows: [
      {
        metricId: 'service-level',
        baselineValue: 100,
        scenarioValue: 99.5,
        direction: 'higher_is_better',
      },
    ],
    financeDefinitionRef: 'finance-ebitda-definition-v1',
    mappedGlAccountIds: ['gl-salaries'],
    financeReconciled: true,
  },
  optionComparison: {
    options: [
      {
        optionId: 'do-nothing',
        optionType: 'do_nothing',
        valueDomain: 'org_cost',
        netRunRateUsd: 0,
        oneTimeCashUsd: 0,
        npvUsd: 0,
        paybackMonth: 0,
        firstBenefitMonth: 0,
        fullRunRateMonth: 0,
        scheduleP50Month: 0,
        scheduleP80Month: 0,
        capacityGapFte: 2,
        serviceDeltaPct: 0,
        legalGate: 'pass',
        confidenceRung: 'R4',
      },
      {
        optionId: 'delayer',
        optionType: 'delayering',
        valueDomain: 'org_cost',
        netRunRateUsd: 125,
        oneTimeCashUsd: 100,
        npvUsd: 98,
        paybackMonth: 8,
        firstBenefitMonth: 2,
        fullRunRateMonth: 6,
        scheduleP50Month: 5,
        scheduleP80Month: 6,
        capacityGapFte: 1,
        serviceDeltaPct: 0,
        legalGate: 'pass',
        confidenceRung: 'R4',
      },
    ],
  },
  savingsGate: {
    contributions: [
      {
        contributionId: 'eligible',
        valueDomain: 'org_cost',
        netRunRateUsd: 100,
        workBound: true,
        resourceOrVendorActionBound: true,
        financeAccountBound: true,
        ownerBound: true,
        milestoneBound: true,
        overlapKeysBound: true,
      },
      {
        contributionId: 'withheld',
        valueDomain: 'org_cost',
        netRunRateUsd: 50,
        workBound: false,
        resourceOrVendorActionBound: true,
        financeAccountBound: true,
        ownerBound: true,
        milestoneBound: true,
        overlapKeysBound: true,
      },
    ],
  },
  peopleRisk: {
    financeReconciledOrgNetRunRateUsd: 1000,
    attritionCohorts: [
      {
        cohortId: 'critical',
        eligibleCount: 100,
        observedExitRateSamples: [0.08, 0.1, 0.12],
        baselineExitRate: 0.05,
        replacementAndRampCostUsd: 10,
      },
    ],
    capacityOrServiceLeakageUsd: 25,
    calibrationDatasetId: 'calibration-1',
  },
  dashboard: {
    identity: {
      optionId: 'delayer',
      optionVersion: 1,
      snapshotId: 'snapshot-1',
      scopeId: 'enterprise',
      currency: 'USD',
      horizonStart: '2026-07-01',
      horizonEnd: '2027-06-30',
    },
    cells: ['economics', 'schedule', 'service'].map((metricKey, index) => ({
      metricKey,
      metricValue: index + 1,
      valueDomain:
        index === 0
          ? ('org_cost' as const)
          : index === 1
            ? ('schedule' as const)
            : ('service' as const),
      optionId: 'delayer',
      optionVersion: 1,
      snapshotId: 'snapshot-1',
      scopeId: 'enterprise',
      currency: 'USD' as const,
      horizonStart: '2026-07-01',
      horizonEnd: '2027-06-30',
      formulaRef: `formula-${metricKey}`,
      evidenceRefId: `evidence-${metricKey}`,
    })),
  },
  recommendation: {
    executiveRationale: 'Choose the option that balances value, execution, and service.',
    tradeoffs: ['Higher execution exposure than do nothing.'],
    decisiveEvidenceRefIds: ['evidence-option-vector'],
    assumptions: ['Approved normalized axes are comparable.'],
    dataGaps: [],
    consequenceOfDelay: 'One month of governed benefit is deferred.',
    ownerRoleId: 'coo',
    controlPlan: 'Review schedule and service controls monthly.',
    decisionPolicy: {
      policyId: 'policy-1',
      policyVersion: 1,
      weights: { value: 0.4, execution: 0.3, service: 0.3 },
    },
    options: [
      {
        optionId: 'do-nothing',
        optionType: 'do_nothing',
        hardGatesPass: true,
        axisScores: { value: 0.2, execution: 0.5, service: 0.7 },
      },
      {
        optionId: 'delayer',
        optionType: 'delayering',
        hardGatesPass: true,
        axisScores: { value: 0.9, execution: 0.6, service: 0.8 },
      },
    ],
  },
  riskModel: {
    executionMode: 'calibrated_distribution',
    confidenceMode: 'calibrated_confidence',
    baseEligibleValueUsd: 120,
    draws: [
      {
        drawId: 'favorable',
        driverId: 'schedule_acceleration',
        jointProbabilityWeight: 1,
        durationMultiplierByWorkPackageId: { design: 1, deploy: 1 },
        valueErosionUsd: 0,
        eligibleRunRateUsd: 80,
        rampFractions: [1],
        recurringLeakageUsdByMonth: [0],
        oneTimeCashUsdByMonth: [0],
      },
      {
        drawId: 'base',
        driverId: 'base',
        jointProbabilityWeight: 1,
        durationMultiplierByWorkPackageId: { design: 1, deploy: 1.5 },
        valueErosionUsd: 10,
        eligibleRunRateUsd: 100,
        rampFractions: [1],
        recurringLeakageUsdByMonth: [0],
        oneTimeCashUsdByMonth: [0],
      },
      {
        drawId: 'delayed',
        driverId: 'deployment_delay',
        jointProbabilityWeight: 1,
        durationMultiplierByWorkPackageId: { design: 1, deploy: 1.75 },
        valueErosionUsd: 20,
        eligibleRunRateUsd: 100,
        rampFractions: [1],
        recurringLeakageUsdByMonth: [0],
        oneTimeCashUsdByMonth: [0],
      },
      {
        drawId: 'downside',
        driverId: 'capacity_risk',
        jointProbabilityWeight: 1,
        durationMultiplierByWorkPackageId: { design: 1, deploy: 2 },
        valueErosionUsd: 30,
        eligibleRunRateUsd: 110,
        rampFractions: [1],
        recurringLeakageUsdByMonth: [0],
        oneTimeCashUsdByMonth: [0],
      },
      {
        drawId: 'severe',
        driverId: 'combined_downside',
        jointProbabilityWeight: 1,
        durationMultiplierByWorkPackageId: { design: 1, deploy: 2.25 },
        valueErosionUsd: 40,
        eligibleRunRateUsd: 120,
        rampFractions: [1],
        recurringLeakageUsdByMonth: [0],
        oneTimeCashUsdByMonth: [0],
      },
    ],
    simulationSeed: 7,
    algorithmVersion: 'sim-v1',
    correlationModelVersion: 'corr-v1',
    distributionCorrelationReceipt: {
      receiptId: 'risk-model-receipt-1',
      distributionDefinitions: [
        {
          riskDriverId: 'schedule_acceleration',
          distributionType: 'empirical_weighted_draws',
          distributionParameters: {
            sampleDrawIds: ['favorable'],
            weightField: 'jointProbabilityWeight',
          },
          correlationGroupId: 'schedule-value-joint',
        },
        {
          riskDriverId: 'deployment_delay',
          distributionType: 'empirical_weighted_draws',
          distributionParameters: {
            sampleDrawIds: ['delayed', 'severe'],
            weightField: 'jointProbabilityWeight',
          },
          correlationGroupId: 'schedule-value-joint',
        },
        {
          riskDriverId: 'capacity_risk',
          distributionType: 'empirical_weighted_draws',
          distributionParameters: {
            sampleDrawIds: ['downside', 'severe'],
            weightField: 'jointProbabilityWeight',
          },
          correlationGroupId: 'schedule-value-joint',
        },
        {
          riskDriverId: 'combined_downside',
          distributionType: 'empirical_weighted_draws',
          distributionParameters: {
            sampleDrawIds: ['severe'],
            weightField: 'jointProbabilityWeight',
          },
          correlationGroupId: 'schedule-value-joint',
        },
      ],
      correlations: [
        {
          leftRiskDriverId: 'deployment_delay',
          rightRiskDriverId: 'capacity_risk',
          coefficient: 0.6,
        },
      ],
      preCorrelatedDrawIds: ['base', 'delayed', 'downside', 'favorable', 'severe'],
    },
    executionCalibrationDatasetId: 'execution-calibration-1',
    realizationCalibrationDatasetId: 'realization-calibration-1',
    observedActualsUsdByMonth: [100],
  },
} satisfies OrgAnalysisScenarioRawSnapshot);

export const ORG_ANALYSIS_ANTI_ALIAS_FIXTURE_INPUTS = compileOrgAnalysisScenarioFormulaInputs(
  ORG_ANALYSIS_ANTI_ALIAS_FIXTURE_RAW_SNAPSHOT
);

const rollupDefinition = createDefinition({
  formulaId: 'org_position_cost_rollup_v1',
  displayName: 'Org position-cost rollup',
  description:
    'Sums one complete governed USD cost row per in-scope position and blocks incomplete or duplicate coverage.',
  inputSchemaVersion: 'org_position_cost_rollup_input_v1',
  outputSchemaVersion: 'org_position_cost_rollup_output_v1',
  execute: executeOrgPositionCostRollup,
  goldenTraces: [
    {
      traceId: 'org_position_cost_rollup_v1:complete:v1',
      input: {
        positionIds: ['p1', 'p2'],
        costRows: [
          { positionId: 'p1', fullyLoadedAnnualCostUsd: 600, currency: 'USD' },
          { positionId: 'p2', fullyLoadedAnnualCostUsd: 400, currency: 'USD' },
        ],
        reportingCutAsOf: '2026-07-10',
      },
      expected: {
        status: 'ok',
        output: {
          primaryMetricKey: 'fully_loaded_org_cost_usd',
          primaryMetricValue: 1000,
          positionCount: 2,
          fullyLoadedAnnualCostUsd: 1000,
          missingCostPositionIds: [],
        },
      },
    },
    {
      traceId: 'org_position_cost_rollup_v1:blocked:missing-cost:v1',
      input: {
        positionIds: ['p1', 'p2'],
        costRows: [{ positionId: 'p1', fullyLoadedAnnualCostUsd: 600, currency: 'USD' }],
        reportingCutAsOf: '2026-07-10',
      },
      expected: {
        status: 'blocked',
        reasonCode: 'missing_formula_input',
        message: 'Org position-cost coverage is incomplete for p2.',
      },
    },
  ],
});

const subLeverNetRunRateContributionDefinition = createDefinition({
  formulaId: 'org_cost_sub_lever_net_run_rate_contribution_v1',
  displayName: 'Org-cost sub-lever net run-rate contribution',
  description:
    'Sums the governed org-cost sub-lever buildup into one citable operating-income contribution: gross annual run-rate cost reduction less the one-time cost to achieve it, per sub-lever. Non-workforce only; headcount-moving levers are refused, never netted.',
  inputSchemaVersion: 'org_cost_sub_lever_net_run_rate_contribution_input_v1',
  outputSchemaVersion: 'org_cost_sub_lever_net_run_rate_contribution_output_v1',
  execute: executeOrgCostSubLeverNetRunRateContribution,
  helpers: [
    ['isFiniteNonNegative', isFiniteNonNegative],
    ['isNonEmptyString', isNonEmptyString],
    ['roundUsd', roundUsd],
  ],
  goldenTraces: [
    {
      // Byte-for-byte reproduction of the in-module sum: two non-workforce
      // sub-levers, each net = gross - one-time, totalled.
      traceId: 'org_cost_sub_lever_net_run_rate_contribution_v1:buildup:v1',
      input: {
        orgStateId: 'org-state-1',
        reportingCutAsOf: '2026-07-10',
        contributions: [
          {
            subLeverId: 'span-consolidation',
            deltaHeadcount: 0,
            grossRunRateCostReductionUsd: 1_000_000,
            oneTimeCostUsd: 250_000,
            currency: 'USD',
          },
          {
            subLeverId: 'vendor-role-insourcing',
            deltaHeadcount: 0,
            grossRunRateCostReductionUsd: 600_000,
            oneTimeCostUsd: 150_000,
            currency: 'USD',
          },
        ],
      },
      expected: {
        status: 'ok',
        output: {
          primaryMetricKey: 'org_cost_sub_lever_net_run_rate_contribution_usd',
          primaryMetricValue: 1_200_000,
          subLeverCount: 2,
          grossRunRateCostReductionUsd: 1_600_000,
          oneTimeCostUsd: 400_000,
          orgCostSubLeverNetRunRateContributionUsd: 1_200_000,
          subLeverIds: ['span-consolidation', 'vendor-role-insourcing'],
        },
      },
    },
    {
      // One-time cost exceeds the run-rate saving: the net is negative and stays
      // negative. The formula never floors a sub-lever at zero.
      traceId: 'org_cost_sub_lever_net_run_rate_contribution_v1:one-time-dominates:v1',
      input: {
        orgStateId: 'org-state-1',
        reportingCutAsOf: '2026-07-10',
        contributions: [
          {
            subLeverId: 'layer-removal',
            deltaHeadcount: 0,
            grossRunRateCostReductionUsd: 400_000,
            oneTimeCostUsd: 900_000,
            currency: 'USD',
          },
        ],
      },
      expected: {
        status: 'ok',
        output: {
          primaryMetricKey: 'org_cost_sub_lever_net_run_rate_contribution_usd',
          primaryMetricValue: -500_000,
          subLeverCount: 1,
          grossRunRateCostReductionUsd: 400_000,
          oneTimeCostUsd: 900_000,
          orgCostSubLeverNetRunRateContributionUsd: -500_000,
          subLeverIds: ['layer-removal'],
        },
      },
    },
    {
      // Workforce fence: a headcount-moving lever belongs to the
      // workforce-reduction claim and must never be blended into this one.
      traceId: 'org_cost_sub_lever_net_run_rate_contribution_v1:blocked:workforce-lever:v1',
      input: {
        orgStateId: 'org-state-1',
        reportingCutAsOf: '2026-07-10',
        contributions: [
          {
            subLeverId: 'span-consolidation',
            deltaHeadcount: 0,
            grossRunRateCostReductionUsd: 1_000_000,
            oneTimeCostUsd: 250_000,
            currency: 'USD',
          },
          {
            subLeverId: 'role-elimination',
            deltaHeadcount: -12,
            grossRunRateCostReductionUsd: 900_000,
            oneTimeCostUsd: 300_000,
            currency: 'USD',
          },
        ],
      },
      expected: {
        status: 'blocked',
        reasonCode: 'never_blend_violation',
        message:
          'Org-cost sub-levers must not move headcount; workforce levers are a separate claim: role-elimination.',
      },
    },
    {
      traceId: 'org_cost_sub_lever_net_run_rate_contribution_v1:blocked:empty-buildup:v1',
      input: {
        orgStateId: 'org-state-1',
        reportingCutAsOf: '2026-07-10',
        contributions: [],
      },
      expected: {
        status: 'blocked',
        reasonCode: 'missing_formula_input',
        message:
          'Org-cost sub-lever contribution requires an org state, a reporting cut, and at least one governed sub-lever row.',
      },
    },
  ],
});

const timeToRealizeDefinition = createDefinition({
  formulaId: 'org_time_to_realize_v1',
  displayName: 'Org option time to realize',
  description:
    'Computes critical-path first-benefit and full-run-rate timing from an integrated governed schedule.',
  inputSchemaVersion: 'org_time_to_realize_input_v1',
  outputSchemaVersion: 'org_time_to_realize_output_v1',
  execute: executeOrgTimeToRealize,
  helpers: [['isNonEmptyString', isNonEmptyString]],
  goldenTraces: [
    {
      traceId: 'org_time_to_realize_v1:integrated:v1',
      input: ORG_ANALYSIS_ANTI_ALIAS_FIXTURE_INPUTS.org_time_to_realize_v1,
      expected: {
        status: 'ok',
        output: {
          primaryMetricKey: 'full_run_rate_day',
          primaryMetricValue: 30,
          firstBenefitDay: 10,
          fullRunRateDay: 30,
          criticalPathWorkPackageIds: ['design', 'deploy'],
          earliestFinishDayByWorkPackageId: { design: 10, deploy: 30 },
          realizationCurve: [
            { workPackageId: 'design', completionDay: 10, cumulativeRunRateFraction: 0.25 },
            { workPackageId: 'deploy', completionDay: 30, cumulativeRunRateFraction: 1 },
          ],
          scheduleGapCodes: [],
        },
      },
    },
    {
      traceId: 'org_time_to_realize_v1:blocked:cycle:v1',
      input: {
        ...ORG_ANALYSIS_ANTI_ALIAS_FIXTURE_INPUTS.org_time_to_realize_v1,
        workPackages: [
          {
            workPackageId: 'a',
            predecessorIds: ['b'],
            durationDays: 10,
            ownerRoleId: 'coo',
            startsBenefit: true,
            requiredForFullRunRate: false,
            cumulativeRunRateFraction: 0.25,
          },
          {
            workPackageId: 'b',
            predecessorIds: ['a'],
            durationDays: 20,
            ownerRoleId: 'coo',
            startsBenefit: false,
            requiredForFullRunRate: true,
            cumulativeRunRateFraction: 1,
          },
        ],
      },
      expected: {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message: 'Time-to-realize schedule contains a dependency cycle.',
      },
    },
  ],
});

const delayeringDefinition = createDefinition({
  formulaId: 'org_delayering_net_ebitda_scenario_v1',
  displayName: 'Org delayering separated economics',
  description:
    'Computes org-only recurring run rate, one-time cash, in-period cash, NPV, capacity, and service outputs without blending workforce dollars.',
  inputSchemaVersion: 'org_delayering_net_ebitda_scenario_input_v1',
  outputSchemaVersion: 'org_delayering_net_ebitda_scenario_output_v1',
  execute: executeOrgDelayeringNetEbitdaScenario,
  helpers: [
    ['roundUsd', roundUsd],
    ['roundMetric', roundMetric],
    ['graphMetrics', graphMetrics],
  ],
  goldenTraces: [
    {
      traceId: 'org_delayering_net_ebitda_scenario_v1:separated:v1',
      input: ORG_ANALYSIS_ANTI_ALIAS_FIXTURE_INPUTS.org_delayering_net_ebitda_scenario_v1,
      expected: {
        status: 'ok',
        output: {
          primaryMetricKey: 'org_delayering_net_run_rate_usd',
          primaryMetricValue: 125,
          baselinePositionCount: 3,
          baselineFullyLoadedOrgCostUsd: 1000,
          substratePositionCount: 11_855,
          substrateFullyLoadedOrgCostUsd: 941_920_000,
          afterPositionCount: 3,
          afterFullyLoadedOrgCostUsd: 850,
          netRunRateUsd: 125,
          oneTimeCashUsd: 100,
          inPeriodNetCashUsd: 100,
          npvUsd: 98.59,
          capacityGapFte: 1,
          serviceDeltaPct: -0.5,
          ebitdaLabel: 'finance_reconciled_net_ebitda',
          financeDefinitionRef: 'finance-ebitda-definition-v1',
          mappedGlAccountIds: ['gl-salaries'],
          graphDelta: {
            beforePositionCount: 3,
            afterPositionCount: 3,
            beforeManagerCount: 2,
            afterManagerCount: 2,
            beforeMaxDepth: 3,
            afterMaxDepth: 3,
            removedPositionIds: ['analyst'],
            addedPositionIds: ['coordinator'],
            reparentedPositionIds: [],
          },
        },
      },
    },
    {
      traceId: 'org_delayering_net_ebitda_scenario_v1:blocked:over-substrate:v1',
      input: {
        ...ORG_ANALYSIS_ANTI_ALIAS_FIXTURE_INPUTS.org_delayering_net_ebitda_scenario_v1,
        baselinePositions: [
          {
            positionId: 'root',
            managerPositionId: null,
            fullyLoadedAnnualCostUsd: 600,
            assignedWorkPackageIds: [],
          },
          {
            positionId: 'analyst',
            managerPositionId: 'root',
            fullyLoadedAnnualCostUsd: 200,
            assignedWorkPackageIds: ['required-work'],
          },
        ],
        actions: [
          {
            actionId: 'remove-analyst',
            actionType: 'remove_position',
            positionId: 'analyst',
            targetManagerPositionId: null,
            fullyLoadedAnnualCostUsd: null,
            assignedWorkPackageIds: [],
            reassignWorkToPositionId: null,
          },
        ],
      },
      expected: {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message: 'Delayering actions cannot orphan required work.',
      },
    },
  ],
});

const compareDefinition = createDefinition({
  formulaId: 'org_compare_cost_structure_options_v1',
  displayName: 'Org cost-structure option comparison',
  description:
    'Computes a component-wise Pareto set without summing org and workforce option dollars.',
  inputSchemaVersion: 'org_compare_cost_structure_options_input_v1',
  outputSchemaVersion: 'org_compare_cost_structure_options_output_v1',
  execute: executeOrgCompareCostStructureOptions,
  helpers: [
    ['optionDominates', optionDominates],
    ['confidenceRank', confidenceRank],
  ],
  goldenTraces: [
    {
      traceId: 'org_compare_cost_structure_options_v1:pareto:v1',
      input: ORG_ANALYSIS_ANTI_ALIAS_FIXTURE_INPUTS.org_compare_cost_structure_options_v1,
      expected: {
        status: 'ok',
        output: {
          primaryMetricKey: 'pareto_option_count',
          primaryMetricValue: 2,
          optionCount: 2,
          paretoOptionIds: ['delayer', 'do-nothing'],
          blockedOptionIds: [],
          options: [
            {
              optionId: 'do-nothing',
              optionType: 'do_nothing',
              valueDomain: 'org_cost',
              netRunRateUsd: 0,
              oneTimeCashUsd: 0,
              npvUsd: 0,
              paybackMonth: 0,
              firstBenefitMonth: 0,
              fullRunRateMonth: 0,
              scheduleP50Month: 0,
              scheduleP80Month: 0,
              capacityGapFte: 2,
              serviceDeltaPct: 0,
              legalGate: 'pass',
              confidenceRung: 'R4',
            },
            {
              optionId: 'delayer',
              optionType: 'delayering',
              valueDomain: 'org_cost',
              netRunRateUsd: 125,
              oneTimeCashUsd: 100,
              npvUsd: 98,
              paybackMonth: 8,
              firstBenefitMonth: 2,
              fullRunRateMonth: 6,
              scheduleP50Month: 5,
              scheduleP80Month: 6,
              capacityGapFte: 1,
              serviceDeltaPct: 0,
              legalGate: 'pass',
              confidenceRung: 'R4',
            },
          ],
          additiveValueUsd: null,
        },
      },
    },
    {
      traceId: 'org_compare_cost_structure_options_v1:blocked:no-comparator:v1',
      input: {
        ...ORG_ANALYSIS_ANTI_ALIAS_FIXTURE_INPUTS.org_compare_cost_structure_options_v1,
        options: [
          ORG_ANALYSIS_ANTI_ALIAS_FIXTURE_INPUTS.org_compare_cost_structure_options_v1.options[1],
          {
            ...ORG_ANALYSIS_ANTI_ALIAS_FIXTURE_INPUTS.org_compare_cost_structure_options_v1
              .options[1],
            optionId: 'shared-service',
            optionType: 'shared_services',
          },
        ],
      },
      expected: {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message:
          'Cost-structure options require unique ids, a do-nothing comparator, and finite vector values.',
      },
    },
  ],
});

const savingsDefinition = createDefinition({
  formulaId: 'org_savings_work_redesign_gate_v1',
  displayName: 'Org savings work-redesign gate',
  description:
    'Sums only org-cost leaves with all six bankability predicates and blocks workforce-dollar blending.',
  inputSchemaVersion: 'org_savings_work_redesign_gate_input_v1',
  outputSchemaVersion: 'org_savings_work_redesign_gate_output_v1',
  execute: executeOrgSavingsWorkRedesignGate,
  helpers: [['WORK_REDIESIGN_PREDICATES', WORK_REDIESIGN_PREDICATES]],
  goldenTraces: [
    {
      traceId: 'org_savings_work_redesign_gate_v1:eligible-and-withheld:v1',
      input: ORG_ANALYSIS_ANTI_ALIAS_FIXTURE_INPUTS.org_savings_work_redesign_gate_v1,
      expected: {
        status: 'ok',
        output: {
          primaryMetricKey: 'bankable_org_net_run_rate_usd',
          primaryMetricValue: 100,
          bankableNetRunRateUsd: 100,
          withheldNetRunRateUsd: 50,
          eligibleContributionIds: ['eligible'],
          withheldPredicatesByContributionId: { withheld: ['work_disposition_missing'] },
        },
      },
    },
    {
      traceId: 'org_savings_work_redesign_gate_v1:blocked:workforce-dollar:v1',
      input: {
        ...ORG_ANALYSIS_ANTI_ALIAS_FIXTURE_INPUTS.org_savings_work_redesign_gate_v1,
        contributions: [
          {
            contributionId: 'rif',
            valueDomain: 'workforce',
            netRunRateUsd: 100,
            workBound: true,
            resourceOrVendorActionBound: true,
            financeAccountBound: true,
            ownerBound: true,
            milestoneBound: true,
            overlapKeysBound: true,
          },
        ],
      },
      expected: {
        status: 'blocked',
        reasonCode: 'never_blend_violation',
        message: 'Org-cost work-redesign value must never sum workforce-owned dollars.',
      },
    },
  ],
});

const peopleRiskDefinition = createDefinition({
  formulaId: 'org_people_risk_adjusted_net_ebitda_v1',
  displayName: 'Org people-risk adjusted net run rate',
  description:
    'Subtracts calibrated aggregate attrition and capacity/service leakage from finance-reconciled org value without person scores.',
  inputSchemaVersion: 'org_people_risk_adjusted_net_ebitda_input_v1',
  outputSchemaVersion: 'org_people_risk_adjusted_net_ebitda_output_v1',
  execute: executeOrgPeopleRiskAdjustedNetEbitda,
  helpers: [['roundUsd', roundUsd]],
  goldenTraces: [
    {
      traceId: 'org_people_risk_adjusted_net_ebitda_v1:cohort:v1',
      input: ORG_ANALYSIS_ANTI_ALIAS_FIXTURE_INPUTS.org_people_risk_adjusted_net_ebitda_v1,
      expected: {
        status: 'ok',
        output: {
          primaryMetricKey: 'people_risk_adjusted_org_net_run_rate_usd',
          primaryMetricValue: 925,
          financeReconciledOrgNetRunRateUsd: 1000,
          expectedIncrementalAttritionCostUsd: 50,
          capacityOrServiceLeakageUsd: 25,
          riskAdjustedNetRunRateUsd: 925,
          cohortIncrementalExitCostUsd: { critical: 50 },
          cohortIncrementalExitCostDistributionUsd: { critical: [30, 50, 70] },
          aggregateIncrementalExitCostDistributionUsd: [30, 50, 70],
        },
      },
    },
    {
      traceId: 'org_people_risk_adjusted_net_ebitda_v1:blocked:no-calibration:v1',
      input: {
        ...ORG_ANALYSIS_ANTI_ALIAS_FIXTURE_INPUTS.org_people_risk_adjusted_net_ebitda_v1,
        calibrationDatasetId: '',
      },
      expected: {
        status: 'blocked',
        reasonCode: 'missing_formula_input',
        message:
          'People-risk adjustment requires finance-reconciled org value and calibrated cohort evidence.',
      },
    },
  ],
});

const dashboardDefinition = createDefinition({
  formulaId: 'org_options_decision_dashboard_v1',
  displayName: 'Org options decision dashboard projection',
  description:
    'Projects typed identity-consistent option cells and never creates an additive cross-domain value.',
  inputSchemaVersion: 'org_options_decision_dashboard_input_v1',
  outputSchemaVersion: 'org_options_decision_dashboard_output_v1',
  execute: executeOrgOptionsDecisionDashboard,
  goldenTraces: [
    {
      traceId: 'org_options_decision_dashboard_v1:three-cells:v1',
      input: ORG_ANALYSIS_ANTI_ALIAS_FIXTURE_INPUTS.org_options_decision_dashboard_v1,
      expected: {
        status: 'ok',
        output: {
          primaryMetricKey: 'identity_consistent_dashboard_metric_count',
          primaryMetricValue: 3,
          projectedMetricCount: 3,
          metricKeys: ['economics', 'schedule', 'service'],
          identity: {
            optionId: 'delayer',
            optionVersion: 1,
            snapshotId: 'snapshot-1',
            scopeId: 'enterprise',
            currency: 'USD',
            horizonStart: '2026-07-01',
            horizonEnd: '2027-06-30',
          },
          cells: ['economics', 'schedule', 'service'].map((metricKey, index) => ({
            metricKey,
            metricValue: index + 1,
            valueDomain:
              index === 0
                ? ('org_cost' as const)
                : index === 1
                  ? ('schedule' as const)
                  : ('service' as const),
            optionId: 'delayer',
            optionVersion: 1,
            snapshotId: 'snapshot-1',
            scopeId: 'enterprise',
            currency: 'USD' as const,
            horizonStart: '2026-07-01',
            horizonEnd: '2027-06-30',
            formulaRef: `formula-${metricKey}`,
            evidenceRefId: `evidence-${metricKey}`,
          })),
          additiveValueUsd: null,
        },
      },
    },
    {
      traceId: 'org_options_decision_dashboard_v1:blocked:mixed-option:v1',
      input: {
        ...ORG_ANALYSIS_ANTI_ALIAS_FIXTURE_INPUTS.org_options_decision_dashboard_v1,
        cells: [
          {
            ...ORG_ANALYSIS_ANTI_ALIAS_FIXTURE_INPUTS.org_options_decision_dashboard_v1.cells[0],
            optionId: 'other',
          },
        ],
      },
      expected: {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message:
          'Options dashboard cells must have unique metrics and exact option/snapshot/scope/currency/horizon identity.',
      },
    },
  ],
});

const recommendationDefinition = createDefinition({
  formulaId: 'org_recommended_option_rationale_v1',
  displayName: 'Org recommended option policy',
  description:
    'Filters hard-gate failures and applies only approved multi-axis policy weights with a written executive rationale.',
  inputSchemaVersion: 'org_recommended_option_rationale_input_v1',
  outputSchemaVersion: 'org_recommended_option_rationale_output_v1',
  execute: executeOrgRecommendedOptionRationale,
  helpers: [
    ['recommendationOptionDominates', recommendationOptionDominates],
    ['roundMetric', roundMetric],
  ],
  goldenTraces: [
    {
      traceId: 'org_recommended_option_rationale_v1:policy:v1',
      input: ORG_ANALYSIS_ANTI_ALIAS_FIXTURE_INPUTS.org_recommended_option_rationale_v1,
      expected: {
        status: 'ok',
        output: {
          primaryMetricKey: 'recommended_option_policy_score',
          primaryMetricValue: 0.78,
          recommendedOptionId: 'delayer',
          recommendedOptionScore: 0.78,
          eligibleOptionCount: 2,
          paretoOptionIds: ['delayer'],
          rejectedOptionIds: ['do-nothing'],
          policyId: 'policy-1',
          policyVersion: 1,
          executiveRationale: 'Choose the option that balances value, execution, and service.',
          tradeoffs: ['Higher execution exposure than do nothing.'],
          decisiveEvidenceRefIds: ['evidence-option-vector'],
          assumptions: ['Approved normalized axes are comparable.'],
          dataGaps: [],
          consequenceOfDelay: 'One month of governed benefit is deferred.',
          ownerRoleId: 'coo',
          controlPlan: 'Review schedule and service controls monthly.',
        },
      },
    },
    {
      traceId: 'org_recommended_option_rationale_v1:blocked:weights:v1',
      input: {
        ...ORG_ANALYSIS_ANTI_ALIAS_FIXTURE_INPUTS.org_recommended_option_rationale_v1,
        decisionPolicy: { policyId: 'policy-1', policyVersion: 1, weights: { value: 1 } },
      },
      expected: {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message:
          'Recommendation policy weights must cover the approved axes, be non-negative, and sum exactly to one.',
      },
    },
  ],
});

const executionRiskDefinition = createDefinition({
  formulaId: 'org_execution_risk_time_to_realize_v1',
  displayName: 'Org execution risk time to realize',
  description:
    'Computes reproducible calibrated schedule/value percentiles or an explicitly labeled deterministic sensitivity.',
  inputSchemaVersion: 'org_execution_risk_time_to_realize_input_v1',
  outputSchemaVersion: 'org_execution_risk_time_to_realize_output_v1',
  execute: executeOrgExecutionRiskTimeToRealize,
  helpers: [
    ['weightedPercentile', weightedPercentile],
    ['integratedScheduleCompletionDay', integratedScheduleCompletionDay],
    ['hasValidDistributionCorrelationReceipt', hasValidDistributionCorrelationReceipt],
    ['scenarioDistributionCorrelationDigest', scenarioDistributionCorrelationDigest],
    ['fnv1a32', fnv1a32],
    ['canonicalJson', canonicalJson],
    ['canonicalValue', canonicalValue],
  ],
  goldenTraces: [
    {
      traceId: 'org_execution_risk_time_to_realize_v1:calibrated:v1',
      input: ORG_ANALYSIS_ANTI_ALIAS_FIXTURE_INPUTS.org_execution_risk_time_to_realize_v1,
      expected: {
        status: 'ok',
        output: {
          primaryMetricKey: 'execution_risk_completion_day',
          primaryMetricValue: 50,
          posture: 'calibrated_distribution',
          completionP50Day: 45,
          completionP80Day: 50,
          sensitivityDownsideCompletionDay: null,
          valueP20Usd: 80,
          completionDayByDrawId: { favorable: 30, base: 40, delayed: 45, downside: 50, severe: 55 },
          valueUsdByDrawId: { favorable: 120, base: 110, delayed: 100, downside: 90, severe: 80 },
          driverSensitivityById: {
            schedule_acceleration: {
              completionDayDelta: -10,
              valueDownsideUsd: 0,
            },
            deployment_delay: {
              completionDayDelta: 5,
              valueDownsideUsd: 10,
            },
            capacity_risk: {
              completionDayDelta: 10,
              valueDownsideUsd: 20,
            },
            combined_downside: {
              completionDayDelta: 15,
              valueDownsideUsd: 30,
            },
          },
          simulationSeed: 7,
          iterationCount: 5,
          algorithmVersion: 'sim-v1',
          correlationModelVersion: 'corr-v1',
          distributionCorrelationReceipt:
            ORG_ANALYSIS_ANTI_ALIAS_FIXTURE_INPUTS.org_execution_risk_time_to_realize_v1
              .distributionCorrelationReceipt,
        },
      },
    },
    {
      traceId: 'org_execution_risk_time_to_realize_v1:blocked:no-calibration:v1',
      input: {
        ...ORG_ANALYSIS_ANTI_ALIAS_FIXTURE_INPUTS.org_execution_risk_time_to_realize_v1,
        calibrationDatasetId: null,
      },
      expected: {
        status: 'blocked',
        reasonCode: 'missing_formula_input',
        message: 'Calibrated execution percentiles require a calibration dataset.',
      },
    },
  ],
});

const confidenceDefinition = createDefinition({
  formulaId: 'org_realization_confidence_band_v1',
  displayName: 'Org realization value range',
  description:
    'Computes calibrated realization percentiles or labels low/base/high inputs as a scenario range, never false confidence.',
  inputSchemaVersion: 'org_realization_confidence_band_input_v1',
  outputSchemaVersion: 'org_realization_confidence_band_output_v1',
  execute: executeOrgRealizationConfidenceBand,
  helpers: [
    ['percentile', percentile],
    ['weightedPercentile', weightedPercentile],
    ['roundUsd', roundUsd],
    ['hasValidDistributionCorrelationReceipt', hasValidDistributionCorrelationReceipt],
    ['scenarioDistributionCorrelationDigest', scenarioDistributionCorrelationDigest],
    ['fnv1a32', fnv1a32],
    ['canonicalJson', canonicalJson],
    ['canonicalValue', canonicalValue],
  ],
  goldenTraces: [
    {
      traceId: 'org_realization_confidence_band_v1:scenario-range:v1',
      input: ORG_ANALYSIS_ANTI_ALIAS_FIXTURE_INPUTS.org_realization_confidence_band_v1,
      expected: {
        status: 'ok',
        output: {
          primaryMetricKey: 'realization_value_range_width_usd',
          primaryMetricValue: 40,
          posture: 'calibrated_confidence',
          p10Usd: 80,
          p50Usd: 100,
          p90Usd: 120,
          scenarioLowUsd: null,
          scenarioBaseUsd: null,
          scenarioHighUsd: null,
          rangeWidthUsd: 40,
          calibrationDatasetId: 'realization-calibration-1',
          realizedValueByDrawId: {
            favorable: 80,
            base: 100,
            delayed: 100,
            downside: 110,
            severe: 120,
          },
          calibration: { observedMonthCount: 1, baseMeanAbsoluteErrorUsd: 0 },
          simulationSeed: 7,
          iterationCount: 5,
          algorithmVersion: 'sim-v1',
          correlationModelVersion: 'corr-v1',
          inputDrawIds: ['base', 'delayed', 'downside', 'favorable', 'severe'],
          distributionCorrelationReceipt:
            ORG_ANALYSIS_ANTI_ALIAS_FIXTURE_INPUTS.org_realization_confidence_band_v1
              .distributionCorrelationReceipt,
        },
      },
    },
    {
      traceId: 'org_realization_confidence_band_v1:blocked:false-confidence:v1',
      input: {
        ...ORG_ANALYSIS_ANTI_ALIAS_FIXTURE_INPUTS.org_realization_confidence_band_v1,
        observedActualsUsdByMonth: [],
        calibrationDatasetId: null,
      },
      expected: {
        status: 'blocked',
        reasonCode: 'missing_formula_input',
        message: 'Confidence labels require observed monthly actuals and a calibration dataset.',
      },
    },
  ],
});

export const ORG_ANALYSIS_APPROVED_FORMULA_DEFINITIONS: readonly ApprovedFormulaDefinition[] =
  Object.freeze([
    rollupDefinition,
    subLeverNetRunRateContributionDefinition,
    timeToRealizeDefinition,
    delayeringDefinition,
    compareDefinition,
    savingsDefinition,
    peopleRiskDefinition,
    dashboardDefinition,
    recommendationDefinition,
    executionRiskDefinition,
    confidenceDefinition,
  ]);
